/**
 * CCNP ENCOR 350-401 Command Drill Question Bank
 * IOS-XE syntax drill questions for command_builder and speed_round modes.
 */

// Increment this when adding/modifying questions so auto-merge picks up changes
export const CONTENT_VERSION = 4;

export function getQuestionBank() {
  return [
    // ─────────────────────────────────────────────
    // OSPF  (10 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-001",
      domain: "3.0",
      category: "OSPF",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Enable OSPF process 1 on a router and advertise the 192.168.10.0/24 network into area 0.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "router ospf 1",
            "network 192.168.10.0 0.0.0.255 area 0",
          ],
          explanation:
            "The 'router ospf <process-id>' command creates the OSPF process. " +
            "The 'network' statement uses a wildcard mask (inverse of subnet mask) to match interfaces.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "255\\.255\\.255\\.0",
          feedback:
            "OSPF network statements use wildcard masks, not subnet masks. " +
            "For /24 use 0.0.0.255, not 255.255.255.0.",
        },
      ],
      hints: [
        "OSPF uses wildcard masks in network statements",
        "Process IDs are locally significant only",
      ],
      tags: ["ospf", "process", "network-statement", "area"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-002",
      domain: "3.0",
      category: "OSPF",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Set the OSPF reference bandwidth to 10000 Mbps (10 Gbps) so that FastEthernet links " +
        "receive an appropriate cost. You are already inside OSPF process 1.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["auto-cost reference-bandwidth 10000"],
          explanation:
            "The default reference bandwidth is 100 Mbps, which gives both FastEthernet and " +
            "GigabitEthernet a cost of 1. Setting it to 10000 (Mbps) differentiates modern link speeds.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "reference-bandwidth 10",
          feedback:
            "The unit for auto-cost reference-bandwidth is Mbps. " +
            "For 10 Gbps enter 10000, not 10.",
        },
      ],
      hints: ["The value is expressed in Mbps", "Default is 100 Mbps"],
      tags: ["ospf", "reference-bandwidth", "cost", "tuning"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-003",
      domain: "3.0",
      category: "OSPF",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "On interface GigabitEthernet0/1, set the OSPF cost to 50, the OSPF priority to 100, " +
        "and configure the link as a point-to-point network type.",
      context_mode: "Router(config-if)#",
      accepted_answers: [
        {
          commands: [
            "ip ospf cost 50",
            "ip ospf priority 100",
            "ip ospf network point-to-point",
          ],
          explanation:
            "Interface-level OSPF parameters override process-level defaults. " +
            "Point-to-point network type eliminates the DR/BDR election, speeding up convergence on links " +
            "with exactly two routers.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ospf priority",
          feedback:
            "The full command is 'ip ospf priority <value>' — include the 'ip' keyword.",
        },
      ],
      hints: [
        "All three are interface-level 'ip ospf ...' commands",
        "Network type affects DR/BDR election",
      ],
      tags: ["ospf", "interface", "cost", "priority", "network-type"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-004",
      domain: "3.0",
      category: "OSPF",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "On interface GigabitEthernet0/0, change the OSPF hello interval to 5 seconds " +
        "and the dead interval to 20 seconds.",
      context_mode: "Router(config-if)#",
      accepted_answers: [
        {
          commands: ["ip ospf hello-interval 5", "ip ospf dead-interval 20"],
          explanation:
            "Hello and dead intervals must match on both ends of the adjacency. " +
            "The dead interval should be at least 4x the hello interval by convention.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ospf hello 5",
          feedback:
            "The correct syntax is 'ip ospf hello-interval <seconds>', not 'ip ospf hello'.",
        },
      ],
      hints: [
        "Both timers are interface-level 'ip ospf ...' commands",
        "Mismatched timers prevent adjacency formation",
      ],
      tags: ["ospf", "timers", "hello", "dead-interval"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-005",
      domain: "3.0",
      category: "OSPF",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Starting from global config, enable MD5 authentication for OSPF area 0 under OSPF " +
        "process 1, then configure the MD5 key on interface GigabitEthernet0/0 using key-id 1 " +
        "and key string 'ENCOR2024'.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "router ospf 1",
            "area 0 authentication message-digest",
            "interface GigabitEthernet0/0",
            "ip ospf message-digest-key 1 md5 ENCOR2024",
          ],
          explanation:
            "Area-level authentication is enabled under the OSPF process. The MD5 key is " +
            "configured on each participating interface. Both neighbors must share the same " +
            "key-id and key string.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "area 0 authentication$",
          feedback:
            "Plain 'area 0 authentication' enables simple (clear-text) authentication. " +
            "Append 'message-digest' for MD5.",
        },
      ],
      hints: [
        "Area authentication goes under 'router ospf'",
        "Interface key goes under the interface with 'ip ospf message-digest-key'",
      ],
      tags: ["ospf", "authentication", "md5", "security"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-006",
      domain: "3.0",
      category: "OSPF",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure area 10 as a stub area inside OSPF process 1. " +
        "Then configure area 20 as a Not-So-Stubby Area (NSSA).",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["area 10 stub", "area 20 nssa"],
          explanation:
            "Stub areas block external (Type-5) LSAs and replace them with a default route. " +
            "NSSA areas block Type-5 LSAs but allow redistribution of external routes as Type-7 LSAs.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "no-summary",
          feedback:
            "'area <id> stub no-summary' creates a totally stubby area (only default route allowed). " +
            "Use just 'area <id> stub' for a regular stub area.",
        },
      ],
      hints: [
        "Both commands are under 'router ospf'",
        "All routers in the area must agree on stub/NSSA",
      ],
      tags: ["ospf", "stub", "nssa", "area-types"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-007",
      domain: "3.0",
      category: "OSPF",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Create an OSPF virtual link between area 0 and area 2. " +
        "The transit area is area 1. The remote ABR's router-id is 2.2.2.2. " +
        "You are configuring the local ABR inside OSPF process 1.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["area 1 virtual-link 2.2.2.2"],
          explanation:
            "A virtual link tunnels OSPF traffic through a transit area to reconnect a " +
            "discontiguous backbone. The command references the transit area and the remote ABR's router-id. " +
            "Both ABRs must configure the virtual link.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "area 2 virtual-link",
          feedback:
            "The area number in the virtual-link command is the TRANSIT area (the area the link passes through), " +
            "not the disconnected area.",
        },
      ],
      hints: [
        "Specify the transit area, not the disconnected area",
        "Use the remote ABR's router-id as the endpoint",
      ],
      tags: ["ospf", "virtual-link", "backbone", "area"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-008",
      domain: "3.0",
      category: "OSPF",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "As an ABR for area 1, summarize the 10.1.0.0/16 address block into area 0 " +
        "within OSPF process 1. Suppress advertisement of the component subnets.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["area 1 range 10.1.0.0 255.255.0.0"],
          explanation:
            "'area range' creates an inter-area summary on an ABR. The component subnets " +
            "are suppressed and a single Type-3 LSA is sent into the backbone.",
          is_primary: true,
        },
        {
          commands: ["area 1 range 10.1.0.0 255.255.0.0 not-advertise"],
          explanation:
            "'not-advertise' suppresses even the summary, useful for filtering specific address ranges.",
          is_primary: false,
        },
      ],
      common_mistakes: [
        {
          pattern: "summary-address",
          feedback:
            "'summary-address' is used on an ASBR for external route summarization. " +
            "For inter-area summarization on an ABR, use 'area <id> range'.",
        },
      ],
      hints: [
        "ABR inter-area summarization uses 'area range'",
        "ASBR external summarization uses 'summary-address'",
      ],
      tags: ["ospf", "summarization", "abr", "area-range"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-009",
      domain: "3.0",
      category: "OSPF",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Display a brief summary of all OSPF neighbors showing their state, " +
        "then display the OSPF link-state database.",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: [
            "show ip ospf neighbor",
            "show ip ospf database",
          ],
          explanation:
            "'show ip ospf neighbor' reveals adjacency state (FULL, 2WAY, EXSTART, etc.). " +
            "'show ip ospf database' lists all LSAs in the LSDB, which is essential for " +
            "verifying correct topology information.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show ospf",
          feedback:
            "IOS-XE requires 'show ip ospf ...', not 'show ospf ...'.",
        },
      ],
      hints: [
        "Both are 'show ip ospf ...' commands",
        "Neighbor state FULL means a complete adjacency",
      ],
      tags: ["ospf", "show", "verification", "lsdb"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-010",
      domain: "3.0",
      category: "OSPF",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure OSPF process 1 to use key-chain authentication on interface " +
        "GigabitEthernet0/2. The key-chain is named 'OSPF-CHAIN'.\n" +
        "You are on the interface.",
      context_mode: "Router(config-if)#",
      accepted_answers: [
        {
          commands: [
            "ip ospf authentication key-chain OSPF-CHAIN",
          ],
          explanation:
            "IOS-XE supports key-chain authentication for OSPF, allowing cryptographic algorithm " +
            "agility (MD5 or SHA variants) defined within the key-chain itself.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "message-digest-key",
          feedback:
            "Key-chain authentication uses 'ip ospf authentication key-chain <name>', " +
            "not 'ip ospf message-digest-key'. The algorithm is configured inside the key-chain.",
        },
      ],
      hints: [
        "Key-chain authentication references the chain name on the interface",
        "The key-chain must be pre-configured with 'key chain' global command",
      ],
      tags: ["ospf", "authentication", "key-chain", "security"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // EIGRP  (6 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-011",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure EIGRP in named mode using the virtual-instance name 'CORP'. " +
        "Set the autonomous system number to 100 and advertise the 10.0.0.0/8 network.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "router eigrp CORP",
            "address-family ipv4 unicast autonomous-system 100",
            "network 10.0.0.0",
          ],
          explanation:
            "Named EIGRP (introduced in IOS 15.0) uses address-family configuration mode and " +
            "supports all classic features plus additional ones like wide metrics.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "router eigrp 100",
          feedback:
            "That is classic (numbered) EIGRP mode. Named mode uses 'router eigrp <NAME>' " +
            "where NAME is an alphanumeric string.",
        },
      ],
      hints: [
        "Named mode uses a string identifier, not a number",
        "The AS number goes inside the address-family statement",
      ],
      tags: ["eigrp", "named-mode", "address-family", "configuration"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-012",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure this router as an EIGRP stub router that advertises only connected and " +
        "summary routes. You are inside 'router eigrp 10'.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["eigrp stub connected summary"],
          explanation:
            "Stub routers do not propagate routes learned from other EIGRP neighbors, " +
            "reducing query scope. 'connected summary' limits advertisements to directly " +
            "connected and summarized prefixes.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "stub$",
          feedback:
            "'eigrp stub' alone advertises only connected routes by default. " +
            "Explicitly adding 'connected summary' makes the intent clear and ensures " +
            "summary routes are also advertised.",
        },
      ],
      hints: [
        "Stub configuration limits query domain in EIGRP",
        "Multiple route types can be listed on the same command",
      ],
      tags: ["eigrp", "stub", "query-domain", "scalability"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-013",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Enable unequal-cost load balancing in EIGRP AS 10. " +
        "Allow paths with a metric up to 3 times the successor's metric to be used. " +
        "You are inside 'router eigrp 10'.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["variance 3"],
          explanation:
            "The 'variance' multiplier allows EIGRP feasible successors whose metric is within " +
            "(variance x successor_metric) to be installed in the routing table for load balancing. " +
            "The route must still satisfy the feasibility condition.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "maximum-paths",
          feedback:
            "'maximum-paths' controls equal-cost load balancing. " +
            "For UNEQUAL-cost load balancing, use 'variance'.",
        },
      ],
      hints: [
        "Variance is a multiplier, not an absolute metric value",
        "The path must still be a feasible successor (AD < successor metric)",
      ],
      tags: ["eigrp", "variance", "load-balancing", "unequal-cost"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-014",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure EIGRP MD5 authentication on interface GigabitEthernet0/0 for AS 10. " +
        "Use key-chain 'EIGRP-KEYS'.",
      context_mode: "Router(config-if)#",
      accepted_answers: [
        {
          commands: [
            "ip authentication mode eigrp 10 md5",
            "ip authentication key-chain eigrp 10 EIGRP-KEYS",
          ],
          explanation:
            "EIGRP interface authentication requires two interface commands: one to set the " +
            "authentication mode (md5) and one to reference the key-chain. " +
            "The AS number is specified in both commands.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ip authentication key-chain eigrp EIGRP-KEYS",
          feedback:
            "The AS number is required between 'eigrp' and the key-chain name: " +
            "'ip authentication key-chain eigrp <AS> <key-chain-name>'.",
        },
      ],
      hints: [
        "Two separate interface commands are required",
        "Include the AS number in both commands",
      ],
      tags: ["eigrp", "authentication", "md5", "key-chain", "security"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-015",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure an EIGRP summary address for 10.1.0.0/16 on interface GigabitEthernet0/1 " +
        "in AS 10.",
      context_mode: "Router(config-if)#",
      accepted_answers: [
        {
          commands: ["ip summary-address eigrp 10 10.1.0.0 255.255.0.0"],
          explanation:
            "Interface-level summary addresses in EIGRP suppress more-specific component routes " +
            "and advertise only the summarized prefix to EIGRP neighbors on that interface. " +
            "A Null0 route is automatically installed to prevent routing loops.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "0\\.0\\.255\\.255",
          feedback:
            "EIGRP summary-address uses a normal subnet mask, not a wildcard mask. " +
            "Use 255.255.0.0 for /16.",
        },
      ],
      hints: [
        "EIGRP summary uses a subnet mask (not a wildcard)",
        "A Null0 discard route is automatically created",
      ],
      tags: ["eigrp", "summarization", "interface", "summary-address"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-016",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Display the EIGRP topology table for AS 10, showing all paths including those " +
        "not currently in the routing table.",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: ["show ip eigrp topology all-links"],
          explanation:
            "'show ip eigrp topology' alone lists successors and feasible successors only. " +
            "Adding 'all-links' reveals every learned path, including those that failed the " +
            "feasibility condition and are therefore not installed.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show eigrp topology",
          feedback:
            "IOS-XE uses 'show ip eigrp topology', not 'show eigrp topology'.",
        },
      ],
      hints: [
        "Use 'all-links' to see paths that failed the feasibility condition",
        "'show ip eigrp neighbors' shows neighbor adjacencies",
      ],
      tags: ["eigrp", "show", "topology-table", "verification"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // BGP  (8 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-017",
      domain: "3.0",
      category: "BGP",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Configure an eBGP session with neighbor 203.0.113.1, which is in AS 65002. " +
        "The local router is in AS 65001. You are already in BGP router configuration mode.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["neighbor 203.0.113.1 remote-as 65002"],
          explanation:
            "eBGP neighbors are in a different AS. IOS-XE requires the 'neighbor remote-as' command " +
            "to define a BGP peer. Because the AS numbers differ, this is an eBGP session.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "neighbor 203\\.0\\.113\\.1 ebgp",
          feedback:
            "There is no 'ebgp' keyword on the neighbor command. eBGP is implied when the " +
            "remote-as differs from the local AS.",
        },
      ],
      hints: [
        "BGP neighbor type is determined by the AS number",
        "The 'remote-as' keyword defines the neighbor's AS",
      ],
      tags: ["bgp", "ebgp", "neighbor", "peering"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-018",
      domain: "3.0",
      category: "BGP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure an iBGP session with neighbor 10.0.0.2 in the same AS 65001. " +
        "Use the Loopback0 interface as the update source.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "neighbor 10.0.0.2 remote-as 65001",
            "neighbor 10.0.0.2 update-source Loopback0",
          ],
          explanation:
            "iBGP peers share the same AS. Using loopback addresses for iBGP is best practice " +
            "for stability. 'update-source' tells BGP which interface to use for TCP sessions. " +
            "IGP must have a route to the loopback for the session to establish.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "update-source loopback0",
          feedback:
            "IOS-XE interface names are case-sensitive in some contexts. " +
            "Use 'Loopback0' with a capital L.",
        },
      ],
      hints: [
        "iBGP = same AS number",
        "Loopback-based iBGP requires 'update-source'",
        "IGP must have a route to the loopback",
      ],
      tags: ["bgp", "ibgp", "loopback", "update-source"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-019",
      domain: "3.0",
      category: "BGP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure this router as a BGP Route Reflector. " +
        "Make neighbor 10.0.1.1 and 10.0.1.2 route-reflector clients within AS 65001.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "neighbor 10.0.1.1 route-reflector-client",
            "neighbor 10.0.1.2 route-reflector-client",
          ],
          explanation:
            "A Route Reflector relaxes the iBGP full-mesh requirement by reflecting routes " +
            "learned from clients to other clients and non-clients. " +
            "Only the RR needs 'route-reflector-client'; clients configure standard iBGP sessions.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "route-reflector server",
          feedback:
            "There is no 'route-reflector server' command. The RR is implicitly the server " +
            "when you configure 'route-reflector-client' for a neighbor.",
        },
      ],
      hints: [
        "Only the Route Reflector needs the 'route-reflector-client' command",
        "Clients do not need any special configuration",
      ],
      tags: ["bgp", "route-reflector", "ibgp", "scalability"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-020",
      domain: "3.0",
      category: "BGP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Route-map 'SET-LP' already exists and contains 'set local-preference 200'. Apply it " +
        "inbound to eBGP neighbor 203.0.113.1 so received routes get local-preference 200. " +
        "You are in BGP router config mode for AS 65001.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "neighbor 203.0.113.1 route-map SET-LP in",
          ],
          explanation:
            "Local-preference is set via a route map applied inbound on the eBGP session. " +
            "The route map must contain 'set local-preference 200'. " +
            "Local-preference is only exchanged between iBGP peers but is set when receiving eBGP routes.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "set local-preference",
          feedback:
            "'set local-preference' goes INSIDE the route map definition, not directly under 'router bgp'.",
        },
      ],
      hints: [
        "Route maps are applied with 'neighbor <ip> route-map <name> in|out'",
        "The 'set local-preference' action is inside the route map",
      ],
      tags: ["bgp", "local-preference", "route-map", "policy"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-021",
      domain: "3.0",
      category: "BGP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Route-map 'SET-MED' already exists and contains 'set metric 50'. Apply it outbound to " +
        "eBGP neighbor 198.51.100.1 so the MED is set on routes sent to that neighbor. " +
        "You are in BGP router config mode for AS 65001.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "neighbor 198.51.100.1 route-map SET-MED out",
          ],
          explanation:
            "MED is sent to eBGP neighbors to influence their choice of entry point into your AS. " +
            "It is configured outbound. Lower MED is preferred. " +
            "The route map contains 'set metric <value>' to set MED.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "route-map SET-MED in",
          feedback:
            "MED is advertised to the NEIGHBOR to influence inbound traffic. " +
            "Apply the route map 'out' (outbound).",
        },
      ],
      hints: [
        "MED influences how a neighbor routes traffic INTO your AS",
        "Apply outbound on the sending side",
      ],
      tags: ["bgp", "med", "route-map", "traffic-engineering"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-022",
      domain: "3.0",
      category: "BGP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure AS-path prepending to prepend AS 65001 twice for routes sent to eBGP neighbor " +
        "198.51.100.1. Build route-map 'PREPEND' (sequence 10) and apply it outbound under " +
        "router bgp 65001. Start from global config.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "route-map PREPEND permit 10",
            "set as-path prepend 65001 65001",
            "router bgp 65001",
            "neighbor 198.51.100.1 route-map PREPEND out",
          ],
          explanation:
            "AS-path prepending lengthens the AS path so upstream peers see the route as less " +
            "preferred. Build the route-map with 'set as-path prepend', then apply it outbound " +
            "on the eBGP neighbor — without the neighbor apply-line, the prepend is never " +
            "attached to any update.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "prepend last-as",
          feedback:
            "'set as-path prepend last-as <n>' is a shortcut to prepend the last AS in the path. " +
            "To prepend a specific AS, list it explicitly: 'set as-path prepend 65001 65001'.",
        },
      ],
      hints: [
        "List the AS number as many times as you want to prepend it",
        "Longer AS-path = less preferred route",
      ],
      tags: ["bgp", "as-path-prepend", "route-map", "traffic-engineering"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-023",
      domain: "3.0",
      category: "BGP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Create a prefix list named 'PERMIT-10' that permits only 10.0.0.0/8 and denies " +
        "all other prefixes. Then apply it as an inbound filter for neighbor 203.0.113.1.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "ip prefix-list PERMIT-10 seq 10 permit 10.0.0.0/8",
            "ip prefix-list PERMIT-10 seq 20 deny 0.0.0.0/0 le 32",
            "router bgp 65001",
            "neighbor 203.0.113.1 prefix-list PERMIT-10 in",
          ],
          explanation:
            "Prefix lists are more efficient than ACLs for BGP filtering. " +
            "An implicit deny all exists at the end, so the explicit deny is optional but shown for clarity. " +
            "The prefix list is applied per-neighbor with 'prefix-list ... in|out'.",
          is_primary: true,
        },
        {
          commands: [
            "ip prefix-list PERMIT-10 seq 10 permit 10.0.0.0/8",
            "router bgp 65001",
            "neighbor 203.0.113.1 prefix-list PERMIT-10 in",
          ],
          explanation:
            "The implicit deny at the end of a prefix list makes an explicit deny unnecessary.",
          is_primary: false,
        },
      ],
      common_mistakes: [
        {
          pattern: "distribute-list",
          feedback:
            "While distribute-lists can filter BGP routes, prefix lists are the preferred " +
            "and more efficient method for BGP prefix filtering.",
        },
      ],
      hints: [
        "Prefix lists have an implicit deny all at the end",
        "Apply with 'neighbor <ip> prefix-list <name> in|out'",
      ],
      tags: ["bgp", "prefix-list", "filtering", "inbound"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-024",
      domain: "3.0",
      category: "BGP",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Display a summary of all BGP neighbors and their session states. " +
        "Then display the BGP routing table.",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: [
            "show bgp ipv4 unicast summary",
            "show bgp ipv4 unicast",
          ],
          explanation:
            "'show bgp ipv4 unicast summary' provides a concise view of all BGP peers, " +
            "their AS, state, and prefix counts. " +
            "'show bgp ipv4 unicast' shows the full BGP table with path attributes.",
          is_primary: true,
        },
        {
          commands: [
            "show ip bgp summary",
            "show ip bgp",
          ],
          explanation:
            "Legacy 'show ip bgp' commands still work in IOS-XE but the 'show bgp ipv4 unicast' " +
            "form is preferred in modern IOS-XE.",
          is_primary: false,
        },
      ],
      common_mistakes: [
        {
          pattern: "show bgp neighbors",
          feedback:
            "'show bgp neighbors' shows detailed session information for all neighbors. " +
            "'show bgp ipv4 unicast summary' gives the concise state table typically needed.",
        },
      ],
      hints: [
        "The 'summary' keyword gives a one-line-per-neighbor view",
        "Both 'show bgp ipv4 unicast' and 'show ip bgp' work on IOS-XE",
      ],
      tags: ["bgp", "show", "verification", "summary"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // STP  (4 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-025",
      domain: "2.0",
      category: "STP",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Configure this switch to be the primary root bridge for VLAN 10 and the secondary " +
        "root bridge for VLAN 20 using spanning-tree macros.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "spanning-tree vlan 10 root primary",
            "spanning-tree vlan 20 root secondary",
          ],
          explanation:
            "'root primary' sets the priority to 24576 (or lowers it by 4096 if needed) to " +
            "ensure root bridge election. 'root secondary' sets priority to 28672 as a backup.",
          is_primary: true,
        },
        {
          commands: [
            "spanning-tree vlan 10 priority 24576",
            "spanning-tree vlan 20 priority 28672",
          ],
          explanation:
            "Manually setting the priority achieves the same result. " +
            "Priorities must be multiples of 4096.",
          is_primary: false,
        },
      ],
      common_mistakes: [
        {
          pattern: "priority 1",
          feedback:
            "STP bridge priorities must be multiples of 4096 (0, 4096, 8192, ... 61440). " +
            "Use 'spanning-tree vlan <id> root primary' or a valid priority value.",
        },
      ],
      hints: [
        "Root primary = priority 24576; Root secondary = 28672",
        "Priorities must be multiples of 4096",
      ],
      tags: ["stp", "root-bridge", "priority", "vlan"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-026",
      domain: "2.0",
      category: "STP",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Enable PortFast on interface GigabitEthernet1/0/5 and then enable BPDU Guard on " +
        "the same interface to protect against rogue switches.",
      context_mode: "Switch(config-if)#",
      accepted_answers: [
        {
          commands: [
            "spanning-tree portfast",
            "spanning-tree bpduguard enable",
          ],
          explanation:
            "PortFast skips Listening and Learning states, bringing the port up immediately. " +
            "BPDU Guard errdisables the port if any BPDU is received, preventing unauthorized " +
            "switches from connecting to access ports.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "portfast bpduguard",
          feedback:
            "PortFast and BPDU Guard are separate interface-level commands. " +
            "There is no combined 'portfast bpduguard' command.",
        },
      ],
      hints: [
        "Both commands start with 'spanning-tree'",
        "PortFast should only be used on end-device ports",
      ],
      tags: ["stp", "portfast", "bpduguard", "access-port", "security"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-027",
      domain: "2.0",
      category: "STP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Enable BPDU Guard globally on all PortFast-enabled ports and enable Root Guard " +
        "on interface GigabitEthernet1/0/1 to prevent it from becoming a root port.",
      context_mode: "Switch(config)# / Switch(config-if)#",
      accepted_answers: [
        {
          commands: [
            "spanning-tree portfast bpduguard default",
            "interface GigabitEthernet1/0/1",
            "spanning-tree guard root",
          ],
          explanation:
            "'portfast bpduguard default' globally enables BPDU Guard on all PortFast ports. " +
            "Root Guard on an interface prevents that port from ever becoming a root port, " +
            "protecting the root bridge placement.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "spanning-tree bpduguard default",
          feedback:
            "The global command is 'spanning-tree portfast bpduguard default', not just " +
            "'spanning-tree bpduguard default'.",
        },
      ],
      hints: [
        "Global BPDU Guard uses the 'portfast bpduguard default' syntax",
        "Root Guard is configured per-interface with 'spanning-tree guard root'",
      ],
      tags: ["stp", "bpduguard", "root-guard", "global", "security"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-028",
      domain: "2.0",
      category: "STP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure Multiple Spanning Tree (MST) on a switch. " +
        "Create MST instance 1 and map VLANs 10-20 to it. " +
        "Set the region name to 'CORP' and revision number to 1. " +
        "Then activate MST mode.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "spanning-tree mode mst",
            "spanning-tree mst configuration",
            "name CORP",
            "revision 1",
            "instance 1 vlan 10-20",
            "exit",
          ],
          explanation:
            "MST requires all switches in the same region to share identical name, revision, " +
            "and instance-to-VLAN mapping. 'spanning-tree mode mst' activates MST globally. " +
            "Changes inside 'spanning-tree mst configuration' are staged until you exit the " +
            "submode — that's when the region is committed.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "spanning-tree mst 1 vlan 10-20",
          feedback:
            "VLAN-to-instance mapping is configured inside the 'spanning-tree mst configuration' " +
            "submode, not as a direct global command.",
        },
      ],
      hints: [
        "MST configuration enters a submode with 'spanning-tree mst configuration'",
        "Name, revision, and instance mapping must match on all switches in the region",
      ],
      tags: ["stp", "mst", "mstp", "instance", "vlan-mapping"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // FHRP / Layer 3 Services  (4 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-029",
      domain: "3.0",
      category: "FHRP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure HSRPv2 on interface GigabitEthernet0/0 for group 10. " +
        "Set the virtual IP to 192.168.1.1, the priority to 110, and enable preemption. " +
        "Use MD5 authentication with key string 'HSRP-KEY'.",
      context_mode: "Router(config-if)#",
      accepted_answers: [
        {
          commands: [
            "standby version 2",
            "standby 10 ip 192.168.1.1",
            "standby 10 priority 110",
            "standby 10 preempt",
            "standby 10 authentication md5 key-string HSRP-KEY",
          ],
          explanation:
            "HSRPv2 supports IPv6, group numbers 0-4095, and uses multicast 224.0.0.102. " +
            "Priority >100 makes this router the active HSRP router. " +
            "Preemption ensures this router reclaims active status if it was previously active.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "standby 10 ip 192\\.168\\.1\\.1.*priority",
          feedback:
            "The 'standby version 2' command should be configured before other standby group commands.",
        },
      ],
      hints: [
        "Set 'standby version 2' first",
        "Higher priority = active router (default is 100)",
        "Preempt must be explicitly configured",
      ],
      tags: ["hsrp", "fhrp", "v2", "preempt", "authentication"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-030",
      domain: "3.0",
      category: "FHRP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure VRRPv3 on interface GigabitEthernet0/1 for group 5. " +
        "Set the virtual IP to 10.10.10.254 and the priority to 150.",
      context_mode: "Router(config-if)#",
      accepted_answers: [
        {
          commands: [
            "vrrp 5 address-family ipv4",
            "address 10.10.10.254",
            "priority 150",
          ],
          explanation:
            "VRRPv3 on IOS-XE uses 'vrrp <grp> address-family ipv4|ipv6' to enter a per-group " +
            "submode where 'address' and 'priority' are configured. The router with the highest " +
            "priority becomes Master; priority 255 means the router owns the virtual IP and is " +
            "always Master. (Note: 'fhrp version vrrp v3' must be enabled globally first — " +
            "treated as a prerequisite here.)",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "standby",
          feedback:
            "'standby' is the HSRP keyword. VRRP uses the 'vrrp' keyword.",
        },
      ],
      hints: [
        "VRRP uses 'vrrp' not 'standby'",
        "VRRPv3 uses address-family submode",
        "Priority 255 = IP address owner, always becomes master",
      ],
      tags: ["vrrp", "fhrp", "priority", "ipv4"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-031",
      domain: "3.0",
      category: "IP Services",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Configure a DHCP relay on interface GigabitEthernet0/0 to forward DHCP broadcasts " +
        "to the DHCP server at 10.1.1.10.",
      context_mode: "Router(config-if)#",
      accepted_answers: [
        {
          commands: ["ip helper-address 10.1.1.10"],
          explanation:
            "'ip helper-address' converts DHCP broadcasts (and other UDP broadcasts) to unicast " +
            "and forwards them to the specified server. Multiple helper-address commands can be " +
            "used for redundant servers.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ip dhcp relay",
          feedback:
            "IOS-XE uses 'ip helper-address', not 'ip dhcp relay'. " +
            "Helper-address forwards several UDP services by default, not just DHCP.",
        },
      ],
      hints: [
        "Configured on the router interface facing the clients",
        "Can specify multiple helper-address entries for redundancy",
      ],
      tags: ["dhcp", "relay", "helper-address", "layer3-services"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-032",
      domain: "3.0",
      category: "IP Services",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure NTP on a router to synchronize with server 192.168.1.100 using authenticated " +
        "key 1 (MD5 string 'NTPKEY'). Set this router as a stratum 3 NTP master (fallback when " +
        "the upstream is unreachable).",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "ntp authentication-key 1 md5 NTPKEY",
            "ntp authenticate",
            "ntp trusted-key 1",
            "ntp server 192.168.1.100 key 1",
            "ntp master 3",
          ],
          explanation:
            "For authenticated NTP: define the key, enable authentication, mark the key as " +
            "trusted, then BIND the key to the server on the 'ntp server ... key 1' line — " +
            "otherwise the configured key is never actually used when syncing. 'ntp master 3' " +
            "makes the router serve as stratum 3 if upstream sources are unreachable.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ntp master$",
          feedback:
            "'ntp master' without a stratum defaults to stratum 8. " +
            "Specify the stratum explicitly with 'ntp master <stratum>'.",
        },
      ],
      hints: [
        "NTP authentication requires three commands: authenticate, authentication-key, trusted-key",
        "Lower stratum = more authoritative",
      ],
      tags: ["ntp", "authentication", "stratum", "time-sync"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // Security  (4 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-033",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure AAA using TACACS+ for authentication and authorization. Define a TACACS+ " +
        "server 'TACACS-SRV' at 10.1.1.50 with key 'TAC-KEY'. Create a default login " +
        "authentication list AND a default exec authorization list that both try TACACS+ first, " +
        "then local.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "aaa new-model",
            "tacacs server TACACS-SRV",
            "address ipv4 10.1.1.50",
            "key TAC-KEY",
            "aaa authentication login default group tacacs+ local",
            "aaa authorization exec default group tacacs+ local",
          ],
          explanation:
            "'aaa new-model' must be the first AAA command. The TACACS+ server block defines " +
            "server parameters. 'aaa authentication login' controls who can log in; 'aaa " +
            "authorization exec' controls whether the authenticated user is permitted an EXEC " +
            "shell. Both lists fall back to 'local' if TACACS+ is unreachable.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "tacacs-server host",
          feedback:
            "'tacacs-server host' is the legacy syntax. " +
            "IOS-XE uses the 'tacacs server <name>' block configuration style.",
        },
      ],
      hints: [
        "'aaa new-model' must come first",
        "Use the server block syntax for TACACS+: 'tacacs server <name>'",
        "Always include 'local' as a fallback",
      ],
      tags: ["aaa", "tacacs+", "authentication", "security"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-034",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "You are already inside interface GigabitEthernet1/0/10 on a switch. Enable 802.1X " +
        "authenticator role and set the port to auto mode so it requires successful " +
        "authentication before allowing access.",
      context_mode: "Switch(config-if)#",
      accepted_answers: [
        {
          commands: [
            "dot1x pae authenticator",
            "authentication port-control auto",
          ],
          explanation:
            "'dot1x pae authenticator' enables the 802.1X authenticator role on the interface. " +
            "'authentication port-control auto' sets the controlled port to require successful " +
            "authentication before granting network access.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "dot1x port-control auto",
          feedback:
            "On modern IOS-XE, the port control command is " +
            "'authentication port-control auto', not 'dot1x port-control auto'.",
        },
      ],
      hints: [
        "802.1X requires 'dot1x pae authenticator' on the interface",
        "Port control mode 'auto' requires authentication",
      ],
      tags: ["802.1x", "dot1x", "authentication", "port-security"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-035",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Create an extended ACL named 'BLOCK-TELNET' that denies Telnet (TCP port 23) " +
        "from any source to host 10.10.10.1, and permits all other traffic. " +
        "Apply it inbound on interface GigabitEthernet0/0.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "ip access-list extended BLOCK-TELNET",
            "deny tcp any host 10.10.10.1 eq 23",
            "permit ip any any",
            "interface GigabitEthernet0/0",
            "ip access-group BLOCK-TELNET in",
          ],
          explanation:
            "Named extended ACLs match on protocol, source, destination, and port. " +
            "The implicit deny at the end of every ACL means a final 'permit ip any any' " +
            "is required if you want to allow all other traffic. " +
            "ACLs are applied to interfaces with 'ip access-group'.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "eq telnet",
          feedback:
            "'eq telnet' is valid but 'eq 23' is more explicit and unambiguous. " +
            "Both forms are accepted on IOS-XE.",
        },
      ],
      hints: [
        "Extended ACLs go in 'ip access-list extended <name>' mode",
        "Don't forget 'permit ip any any' before the implicit deny",
        "'ip access-group' applies the ACL to an interface",
      ],
      tags: ["acl", "extended", "telnet", "security", "filtering"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-036",
      domain: "5.0",
      category: "Security",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure a basic Control Plane Policing (CoPP) policy that:\n" +
        "- Creates a class-map named 'MGMT-TRAFFIC' matching ACL 'MGMT-ACL'\n" +
        "- Creates a policy-map named 'COPP-POLICY' that polices MGMT-TRAFFIC to 64000 bps\n" +
        "- Applies the policy to the control plane",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "class-map match-all MGMT-TRAFFIC",
            "match access-group name MGMT-ACL",
            "policy-map COPP-POLICY",
            "class MGMT-TRAFFIC",
            "police rate 64000 bps",
            "control-plane",
            "service-policy input COPP-POLICY",
          ],
          explanation:
            "CoPP protects the route processor from DoS attacks by rate-limiting traffic destined " +
            "for the control plane. The policy is applied to the 'control-plane' entity with " +
            "'service-policy input'. Traffic exceeding the police rate is dropped.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "service-policy output",
          feedback:
            "CoPP is applied as 'service-policy input' on the control-plane entity. " +
            "The input direction refers to traffic entering the control plane (from the data plane).",
        },
      ],
      hints: [
        "CoPP policy is applied under 'control-plane', not an interface",
        "Use 'service-policy input' on the control-plane",
        "Police action defaults to dropping excess traffic",
      ],
      tags: ["copp", "control-plane", "security", "qos", "policy-map"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // Automation  (4 questions — speed_round)
    // ─────────────────────────────────────────────
    {
      id: "cmd-037",
      domain: "6.0",
      category: "Automation",
      difficulty: 1,
      mode: "speed_round",
      prompt: "What HTTP method is used to create a new resource via a REST API?",
      context_mode: null,
      accepted_answers: [
        {
          commands: ["POST"],
          explanation:
            "POST creates new resources. GET retrieves, PUT replaces, PATCH partially updates, " +
            "and DELETE removes a resource.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "PUT",
          feedback:
            "PUT replaces an EXISTING resource entirely. POST creates a NEW resource.",
        },
      ],
      hints: ["Think Create, Read, Update, Delete -> POST, GET, PUT/PATCH, DELETE"],
      tags: ["rest", "api", "http-methods", "automation"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-038",
      domain: "6.0",
      category: "Automation",
      difficulty: 1,
      mode: "speed_round",
      prompt: "What TCP port does NETCONF use by default?",
      context_mode: null,
      accepted_answers: [
        {
          commands: ["830"],
          explanation:
            "NETCONF (RFC 6242) uses TCP port 830 over SSH. " +
            "RESTCONF uses HTTPS (port 443). SNMP uses UDP 161/162.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "22",
          feedback:
            "NETCONF runs OVER SSH but on port 830, not the standard SSH port 22.",
        },
        {
          pattern: "443",
          feedback: "Port 443 is HTTPS, used by RESTCONF — not NETCONF.",
        },
      ],
      hints: ["NETCONF uses SSH but not the default SSH port"],
      tags: ["netconf", "port", "automation", "network-programmability"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-039",
      domain: "6.0",
      category: "Automation",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "In YANG, what node type defines a sequence of entries each identified by one or more key leaves?",
      context_mode: null,
      accepted_answers: [
        {
          commands: ["list"],
          explanation:
            "A YANG 'list' defines a sequence of list entries, each identified by one or more key " +
            "leaf nodes. 'leaf' holds a single value, 'container' groups related nodes, " +
            "and 'leaf-list' is a sequence of leaf values without keys.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "container",
          feedback:
            "A 'container' groups nodes but has no list semantics or keys. " +
            "Use 'list' for multiple keyed instances.",
        },
      ],
      hints: [
        "YANG node types: leaf, leaf-list, container, list",
        "Lists have 'key' statements identifying each entry",
      ],
      tags: ["yang", "data-model", "netconf", "automation"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-040",
      domain: "6.0",
      category: "Automation",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "In an Ansible playbook, what module is used to gather structured facts from a " +
        "Cisco IOS-XE device?",
      context_mode: null,
      accepted_answers: [
        {
          commands: ["cisco.ios.ios_facts"],
          explanation:
            "The 'cisco.ios.ios_facts' module (part of the cisco.ios collection) gathers " +
            "structured device facts such as hostname, interfaces, and OS version from IOS-XE devices.",
          is_primary: true,
        },
        {
          commands: ["ios_facts"],
          explanation:
            "Short-form 'ios_facts' works when the cisco.ios collection is in scope.",
          is_primary: false,
        },
      ],
      common_mistakes: [
        {
          pattern: "setup",
          feedback:
            "'setup' is the generic Ansible facts module for Linux hosts. " +
            "For network devices, use the platform-specific facts module like 'cisco.ios.ios_facts'.",
        },
      ],
      hints: [
        "Cisco IOS modules are in the 'cisco.ios' collection",
        "Facts modules follow the pattern: <platform>_facts",
      ],
      tags: ["ansible", "ios_facts", "automation", "network-programmability"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // Additional questions (41-46) for deeper coverage
    // ─────────────────────────────────────────────

    {
      id: "cmd-041",
      domain: "3.0",
      category: "OSPF",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Redistribute connected routes into OSPF process 1 with a metric of 20 and " +
        "metric-type E1. You are inside 'router ospf 1'.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "redistribute connected metric 20 metric-type 1 subnets",
          ],
          explanation:
            "Redistributing into OSPF creates Type-5 (or Type-7 in NSSA) external LSAs. " +
            "E1 routes accumulate cost as they traverse the OSPF domain (cost = external + internal). " +
            "The 'subnets' keyword is required to redistribute subnets, not just classful networks.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "redistribute connected metric 20$",
          feedback:
            "Without 'subnets', only classful networks are redistributed. " +
            "Always include 'subnets' when redistributing into OSPF.",
        },
      ],
      hints: [
        "Always include 'subnets' to redistribute non-classful routes",
        "E1 = cumulative cost; E2 = fixed external cost (default)",
      ],
      tags: ["ospf", "redistribution", "metric-type", "external"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-042",
      domain: "3.0",
      category: "BGP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure BGP Graceful Restart on a router in AS 65001 and set the restart timer " +
        "to 120 seconds. You are inside 'router bgp 65001'.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "bgp graceful-restart",
            "bgp graceful-restart restart-time 120",
          ],
          explanation:
            "BGP Graceful Restart (RFC 4724) allows a BGP speaker to indicate to its peers that " +
            "it has the capability to preserve forwarding state during a BGP restart. " +
            "This prevents traffic disruption during control-plane restarts.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "graceful-restart 120",
          feedback:
            "The restart timer is set with a separate command: 'bgp graceful-restart restart-time 120'.",
        },
      ],
      hints: [
        "Two commands: enable graceful-restart, then set the timer",
        "Used to maintain forwarding during BGP process restart",
      ],
      tags: ["bgp", "graceful-restart", "high-availability", "nonstop-forwarding"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-043",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure Dynamic ARP Inspection (DAI) for VLAN 10 on a switch and mark " +
        "interface GigabitEthernet1/0/1 (uplink to trusted switch) as a DAI trusted port.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "ip arp inspection vlan 10",
            "interface GigabitEthernet1/0/1",
            "ip arp inspection trust",
          ],
          explanation:
            "DAI validates ARP packets against the DHCP snooping binding table. " +
            "Uplink ports to trusted switches must be marked as trusted to allow ARP traffic " +
            "without validation. Untrusted ports (access ports) have ARP packets validated.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ip arp inspection vlan 10 trust",
          feedback:
            "DAI trust is configured per-interface under the interface, not as part of the " +
            "global 'ip arp inspection vlan' command.",
        },
      ],
      hints: [
        "DAI requires DHCP snooping to be enabled first (or static ARP ACLs)",
        "Trust uplinks, not access ports",
      ],
      tags: ["dai", "arp-inspection", "security", "layer2"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-044",
      domain: "6.0",
      category: "Automation",
      difficulty: 2,
      mode: "speed_round",
      prompt:
        "What RESTCONF HTTP method is equivalent to NETCONF's <edit-config> with operation='replace'?",
      context_mode: null,
      accepted_answers: [
        {
          commands: ["PUT"],
          explanation:
            "RESTCONF PUT replaces the entire target resource, equivalent to NETCONF " +
            "<edit-config> with operation='replace'. PATCH performs a partial update, " +
            "similar to operation='merge'.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "PATCH",
          feedback:
            "PATCH performs a partial update (merge), not a full replace. " +
            "PUT replaces the entire resource.",
        },
        {
          pattern: "POST",
          feedback:
            "POST creates a new resource. PUT replaces an existing resource entirely.",
        },
      ],
      hints: [
        "PUT = full replace, PATCH = partial update (merge)",
        "NETCONF operation='replace' maps to RESTCONF PUT",
      ],
      tags: ["restconf", "http", "netconf", "automation"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-045",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Redistribute OSPF process 1 routes into EIGRP AS 10. " +
        "Set the EIGRP seed metric to: bandwidth=1000 Kbps, delay=1 (tens of microseconds), " +
        "reliability=255, load=1, MTU=1500.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "redistribute ospf 1 metric 1000 1 255 1 1500",
          ],
          explanation:
            "EIGRP requires a seed metric when redistributing from another protocol because " +
            "OSPF costs cannot be directly translated. The five metric components are: " +
            "bandwidth (Kbps), delay (tens of microseconds), reliability (0-255), load (0-255), MTU.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "redistribute ospf 1$",
          feedback:
            "EIGRP redistribution requires an explicit metric. Without 'metric', " +
            "routes will not be redistributed (metric is 0/infinite).",
        },
      ],
      hints: [
        "EIGRP needs 5 metric values: BW, delay, reliability, load, MTU",
        "Reliability 255 = 100% reliable",
      ],
      tags: ["eigrp", "redistribution", "metric", "ospf"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-046",
      domain: "2.0",
      category: "STP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Enable Rapid PVST+ (IEEE 802.1w) as the spanning-tree mode on a Catalyst switch " +
        "and verify the spanning-tree status for VLAN 10.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "spanning-tree mode rapid-pvst",
            "do show spanning-tree vlan 10",
          ],
          explanation:
            "Rapid PVST+ runs a separate 802.1w instance per VLAN. " +
            "'do show spanning-tree vlan 10' verifies the root bridge, port roles, and port states " +
            "without leaving configuration mode.",
          is_primary: true,
        },
        {
          commands: [
            "spanning-tree mode rapid-pvst",
          ],
          explanation:
            "If verification is performed separately from privileged EXEC mode.",
          is_primary: false,
        },
      ],
      common_mistakes: [
        {
          pattern: "spanning-tree mode rapid$",
          feedback:
            "The correct keyword is 'rapid-pvst', not 'rapid'. " +
            "IOS-XE supports: 'pvst', 'rapid-pvst', and 'mst'.",
        },
      ],
      hints: [
        "Rapid PVST+ keyword is 'rapid-pvst'",
        "Use 'do show ...' to run show commands from config mode",
      ],
      tags: ["stp", "rapid-pvst", "802.1w", "verification"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // SYNTAX FIX  (18 questions)
    // ─────────────────────────────────────────────

    // ── OSPF ─────────────────────────────────────

    {
      id: "cmd-sf-001",
      domain: "3.0",
      category: "OSPF",
      difficulty: 2,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this OSPF configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "router ospf 1",
        "network 10.0.0.0 255.255.255.0 area 0",
        "network 10.1.0.0 255.255.0.0 area 1",
        "auto-cost reference-bandwidth 10000",
      ],
      error_lines: [
        {
          original: "network 10.0.0.0 255.255.255.0 area 0",
          corrected: "network 10.0.0.0 0.0.0.255 area 0",
          explanation:
            "OSPF network statements require a wildcard mask (inverse mask), not a subnet mask. " +
            "The wildcard for /24 is 0.0.0.255, not 255.255.255.0.",
        },
        {
          original: "network 10.1.0.0 255.255.0.0 area 1",
          corrected: "network 10.1.0.0 0.0.255.255 area 1",
          explanation:
            "Same issue on the second network statement: /16 requires wildcard 0.0.255.255.",
        },
      ],
      accepted_answers: [
        {
          commands: [
            "network 10.0.0.0 0.0.0.255 area 0",
            "network 10.1.0.0 0.0.255.255 area 1",
          ],
          explanation: "Replace subnet masks with their wildcard (inverse) equivalents.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "Wildcard masks are the bitwise inverse of subnet masks",
        "0.0.0.255 is the wildcard for a /24 prefix",
      ],
      tags: ["ospf", "wildcard-mask", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-sf-002",
      domain: "3.0",
      category: "OSPF",
      difficulty: 3,
      mode: "syntax_fix",
      prompt:
        "This is an internal (non-ABR) router inside OSPF stub area 10. Find and fix the error " +
        "in its stub-area configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "router ospf 1",
        "area 10 stub no-summary",
        "area 10 default-cost 20",
        "network 172.16.0.0 0.0.255.255 area 10",
      ],
      error_lines: [
        {
          original: "area 10 stub no-summary",
          corrected: "area 10 stub",
          explanation:
            "'no-summary' converts a stub area into a Totally Stubby area and is only valid on " +
            "the ABR. Internal routers in the stub area use 'area 10 stub' without 'no-summary'. " +
            "Also, 'area 10 default-cost' only has effect on the ABR.",
        },
      ],
      accepted_answers: [
        {
          commands: ["area 10 stub"],
          explanation:
            "Internal routers in a totally stubby area only need 'area X stub'. " +
            "The 'no-summary' keyword is added only on the ABR.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "'no-summary' on an internal (non-ABR) router does not cause an error but is meaningless",
        "Both ABR and internal routers must agree on 'stub'; only the ABR adds 'no-summary'",
      ],
      tags: ["ospf", "stub-area", "totally-stubby", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-sf-003",
      domain: "3.0",
      category: "OSPF",
      difficulty: 3,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this OSPF MD5 authentication configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "interface GigabitEthernet0/0",
        "ip ospf authentication",
        "ip ospf message-digest-key 1 md5 Cisco123",
        "router ospf 1",
        "area 0 authentication",
      ],
      error_lines: [
        {
          original: "ip ospf authentication",
          corrected: "ip ospf authentication message-digest",
          explanation:
            "'ip ospf authentication' enables plain-text authentication. " +
            "To use MD5 (message-digest) you must add the 'message-digest' keyword. " +
            "Without it the configured md5 key is never used.",
        },
      ],
      accepted_answers: [
        {
          commands: ["ip ospf authentication message-digest"],
          explanation:
            "The 'message-digest' keyword is required to tell OSPF to use the MD5 key " +
            "configured with 'ip ospf message-digest-key'.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "There are two OSPF interface-level auth modes: plain-text and message-digest",
        "The 'message-digest-key' command sets the key; 'authentication message-digest' activates MD5",
      ],
      tags: ["ospf", "authentication", "md5", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ── EIGRP ────────────────────────────────────

    {
      id: "cmd-sf-004",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 3,
      mode: "syntax_fix",
      prompt:
        "Neighbors are configured in EIGRP AS 1. Find and fix the error in this named-mode " +
        "configuration so this router can peer with them:",
      context_mode: "Router(config)#",
      config_block: [
        "router eigrp CORP",
        "address-family ipv4 unicast autonomous-system 100",
        "af-interface GigabitEthernet0/0",
        "passive-interface",
        "exit-af-interface",
        "topology base",
        "redistribute ospf 1 metric 10000 100 255 1 1500",
        "exit-af-topology",
        "network 10.0.0.0",
        "exit-address-family",
      ],
      error_lines: [
        {
          original: "address-family ipv4 unicast autonomous-system 100",
          corrected: "address-family ipv4 unicast autonomous-system 1",
          explanation:
            "EIGRP neighbors must share the same AS number. The neighbors use AS 1, so the " +
            "address-family AS must also be 1. Named-mode EIGRP carries the AS inside the " +
            "address-family statement, not in 'router eigrp'.",
        },
      ],
      accepted_answers: [
        {
          commands: ["address-family ipv4 unicast autonomous-system 1"],
          explanation:
            "All routers in the same EIGRP domain must use the same AS number. " +
            "Correct the AS to match neighbouring routers.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "In named EIGRP the AS number is specified inside the address-family statement",
        "AS number mismatches silently prevent neighbour formation",
      ],
      tags: ["eigrp", "named-mode", "autonomous-system", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-sf-005",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 3,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this EIGRP metric-weights configuration:",
      context_mode: "Router(config-router)#",
      config_block: [
        "router eigrp 10",
        "metric weights 1 1 0 1 0 0",
        "network 192.168.0.0 0.0.255.255",
        "no auto-summary",
      ],
      error_lines: [
        {
          original: "metric weights 1 1 0 1 0 0",
          corrected: "metric weights 0 1 0 1 0 0",
          explanation:
            "The syntax is 'metric weights TOS K1 K2 K3 K4 K5'. The TOS field (first value) " +
            "must always be 0 on IOS-XE — any non-zero value is rejected. Setting it to 1 " +
            "causes the command to be refused or ignored.",
        },
      ],
      accepted_answers: [
        {
          commands: ["metric weights 0 1 0 1 0 0"],
          explanation:
            "TOS must be 0. K1=1 (bandwidth) and K3=1 (delay) are the classic EIGRP defaults.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "Syntax: metric weights <TOS> <K1> <K2> <K3> <K4> <K5>",
        "TOS is always 0 on IOS-XE; it is a legacy field",
      ],
      tags: ["eigrp", "metric-weights", "k-values", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ── BGP ──────────────────────────────────────

    {
      id: "cmd-sf-006",
      domain: "3.0",
      category: "BGP",
      difficulty: 2,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this eBGP neighbour configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "router bgp 65001",
        "neighbor 203.0.113.1 remote-as 65001",
        "neighbor 203.0.113.1 ebgp-multihop 2",
        "neighbor 203.0.113.1 update-source Loopback0",
      ],
      error_lines: [
        {
          original: "neighbor 203.0.113.1 remote-as 65001",
          corrected: "neighbor 203.0.113.1 remote-as 65002",
          explanation:
            "The local router is in AS 65001. Setting remote-as to 65001 makes this an iBGP " +
            "session, not eBGP. For an eBGP peering the remote AS must be different (e.g. 65002). " +
            "ebgp-multihop is also meaningless for iBGP, confirming the intent is eBGP.",
        },
      ],
      accepted_answers: [
        {
          commands: ["neighbor 203.0.113.1 remote-as 65002"],
          explanation:
            "For an eBGP session, the remote-as value must differ from the local AS (65001).",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "eBGP requires the remote AS to be different from the local AS",
        "ebgp-multihop combined with the same AS number is a contradiction",
      ],
      tags: ["bgp", "ebgp", "remote-as", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-sf-007",
      domain: "3.0",
      category: "BGP",
      difficulty: 3,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this BGP route-map application:",
      context_mode: "Router(config)#",
      config_block: [
        "router bgp 65001",
        "neighbor 10.0.0.2 remote-as 65002",
        "neighbor 10.0.0.2 route-map SET-LOCAL-PREF in",
        "neighbor 10.0.0.2 route-map SET-MED in",
        "route-map SET-LOCAL-PREF permit 10",
        "set local-preference 200",
        "route-map SET-MED permit 10",
        "set metric 50",
      ],
      error_lines: [
        {
          original: "neighbor 10.0.0.2 route-map SET-MED in",
          corrected: "neighbor 10.0.0.2 route-map SET-MED out",
          explanation:
            "MED (metric) is an outbound attribute sent to eBGP peers to influence how " +
            "traffic enters your AS. Applying SET-MED in the inbound direction has no effect " +
            "because 'set metric' inside a route-map applied inbound does not set MED on " +
            "received routes. MED manipulation for outbound traffic influence must use 'out'.",
        },
      ],
      accepted_answers: [
        {
          commands: ["neighbor 10.0.0.2 route-map SET-MED out"],
          explanation:
            "MED is advertised outbound to influence inbound traffic from the peer's AS. " +
            "Use 'out' direction for the route-map that sets MED.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "Local Preference is inbound (in) — it influences egress path selection within your AS",
        "MED is outbound (out) — you advertise it to neighbours to influence how they send traffic to you",
      ],
      tags: ["bgp", "route-map", "med", "local-preference", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-sf-008",
      domain: "3.0",
      category: "BGP",
      difficulty: 3,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this BGP IPv6 address-family configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "router bgp 65001",
        "neighbor 2001:db8::2 remote-as 65002",
        "address-family ipv6",
        "neighbor 2001:db8::2 activate",
        "network 2001:db8:1::/48",
        "exit-address-family",
      ],
      error_lines: [
        {
          original: "address-family ipv6",
          corrected: "address-family ipv6 unicast",
          explanation:
            "IOS-XE requires the explicit 'unicast' keyword: 'address-family ipv6 unicast'. " +
            "Omitting it is accepted on some platforms but is not the canonical IOS-XE syntax " +
            "and can cause unexpected behaviour.",
        },
      ],
      accepted_answers: [
        {
          commands: ["address-family ipv6 unicast"],
          explanation:
            "Always specify 'unicast' explicitly in the address-family statement on IOS-XE.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "IOS-XE BGP address-family syntax: 'address-family ipv6 unicast'",
        "IPv6 network statements under address-family use prefix/length — no 'mask' keyword",
      ],
      tags: ["bgp", "ipv6", "address-family", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ── STP ──────────────────────────────────────

    {
      id: "cmd-sf-009",
      domain: "2.0",
      category: "STP",
      difficulty: 2,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this STP bridge-priority configuration:",
      context_mode: "Switch(config)#",
      config_block: [
        "spanning-tree vlan 10 priority 8000",
        "spanning-tree vlan 20 priority 16384",
        "spanning-tree vlan 30 priority 32768",
      ],
      error_lines: [
        {
          original: "spanning-tree vlan 10 priority 8000",
          corrected: "spanning-tree vlan 10 priority 8192",
          explanation:
            "STP bridge priority must be a multiple of 4096. 8000 is not a valid value. " +
            "The closest valid values are 4096 and 8192.",
        },
      ],
      accepted_answers: [
        {
          commands: ["spanning-tree vlan 10 priority 8192"],
          explanation:
            "Valid STP priority values are multiples of 4096: 0, 4096, 8192, 12288, … 61440.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "STP priority increments in steps of 4096",
        "Valid range: 0–61440 in multiples of 4096",
      ],
      tags: ["stp", "bridge-priority", "vlan", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-sf-010",
      domain: "2.0",
      category: "STP",
      difficulty: 2,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this PortFast / BPDU Guard configuration:",
      context_mode: "Switch(config)#",
      config_block: [
        "interface GigabitEthernet0/1",
        "switchport mode trunk",
        "switchport trunk allowed vlan 10,20,30",
        "spanning-tree portfast",
        "spanning-tree bpduguard enable",
      ],
      error_lines: [
        {
          original: "spanning-tree portfast",
          corrected: "spanning-tree portfast trunk",
          explanation:
            "PortFast is designed for access ports connected to end-hosts. Enabling it on a " +
            "trunk port without the 'trunk' keyword generates a warning and can cause bridging " +
            "loops if another switch is connected. On IOS-XE, 'spanning-tree portfast trunk' " +
            "is the correct command to explicitly enable PortFast on a trunk — though this " +
            "should only be done when the trunk leads to a host (e.g. a hypervisor), never to " +
            "another switch.",
        },
      ],
      accepted_answers: [
        {
          commands: ["spanning-tree portfast trunk"],
          explanation:
            "Use 'spanning-tree portfast trunk' when PortFast is intentionally needed on a " +
            "trunk port. Never use this toward another switch.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "Plain 'spanning-tree portfast' is for access ports only",
        "Trunk PortFast requires the explicit 'trunk' keyword on IOS-XE",
      ],
      tags: ["stp", "portfast", "trunk", "bpduguard", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ── ACL ──────────────────────────────────────

    {
      id: "cmd-sf-011",
      domain: "5.0",
      category: "ACL",
      difficulty: 2,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this standard ACL (permit/deny ordering):",
      context_mode: "Router(config)#",
      config_block: [
        "ip access-list standard MGMT-ACCESS",
        "permit any",
        "deny 192.168.99.0 0.0.0.255",
        "permit 10.0.0.0 0.0.0.255",
      ],
      error_lines: [
        {
          original: "permit any",
          corrected: "deny 192.168.99.0 0.0.0.255",
          explanation:
            "ACLs are processed top-down and stop at the first match. 'permit any' on line 1 " +
            "matches all traffic, so the 'deny 192.168.99.0' and 'permit 10.0.0.0' entries that " +
            "follow are never reached. The deny must appear before the broad permit.",
        },
      ],
      accepted_answers: [
        {
          commands: [
            "ip access-list standard MGMT-ACCESS",
            "deny 192.168.99.0 0.0.0.255",
            "permit 10.0.0.0 0.0.0.255",
          ],
          explanation:
            "Place specific deny/permit entries before any broad 'permit any' statement.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "ACLs are first-match: a broad rule early in the list shadows everything below it",
        "Always place specific statements before general ones",
      ],
      tags: ["acl", "permit-deny-order", "standard-acl", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-sf-012",
      domain: "5.0",
      category: "ACL",
      difficulty: 3,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this extended ACL:",
      context_mode: "Router(config)#",
      config_block: [
        "ip access-list extended BLOCK-TELNET",
        "deny tcp 10.0.0.0 255.255.255.0 any eq 23",
        "permit ip any any",
      ],
      error_lines: [
        {
          original: "deny tcp 10.0.0.0 255.255.255.0 any eq 23",
          corrected: "deny tcp 10.0.0.0 0.0.0.255 any eq 23",
          explanation:
            "Extended ACLs also use wildcard masks for source and destination, not subnet masks. " +
            "255.255.255.0 is a subnet mask; the correct wildcard for a /24 is 0.0.0.255.",
        },
      ],
      accepted_answers: [
        {
          commands: ["deny tcp 10.0.0.0 0.0.0.255 any eq 23"],
          explanation:
            "Replace the subnet mask 255.255.255.0 with the wildcard mask 0.0.0.255 in the " +
            "extended ACL source field.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "Both standard and extended ACLs use wildcard masks",
        "0.0.0.255 is the wildcard for /24; 255.255.255.0 is the subnet mask",
      ],
      tags: ["acl", "extended-acl", "wildcard-mask", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ── Security ─────────────────────────────────

    {
      id: "cmd-sf-013",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "syntax_fix",
      prompt: "Find and fix the error in this TACACS+ server configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "aaa new-model",
        "tacacs server ISE-PRIMARY",
        "address ipv4 10.10.10.10",
        "port 1812",
        "key Str0ngK3y!",
        "aaa authentication login default group tacacs+ local",
      ],
      error_lines: [
        {
          original: "port 1812",
          corrected: "port 49",
          explanation:
            "TACACS+ uses TCP port 49. Port 1812 is RADIUS authentication (UDP). " +
            "Using 1812 here means the router will never reach the TACACS+ daemon.",
        },
      ],
      accepted_answers: [
        {
          commands: ["port 49"],
          explanation:
            "TACACS+ uses TCP port 49. RADIUS authentication uses UDP 1812 and accounting UDP 1813 — don't confuse them.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "TACACS+ = TCP port 49",
        "RADIUS authentication = UDP 1812, accounting = UDP 1813",
      ],
      tags: ["security", "tacacs+", "aaa", "port", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-sf-014",
      domain: "5.0",
      category: "Security",
      difficulty: 3,
      mode: "syntax_fix",
      prompt: "Find and fix the error in this 802.1X switch-port configuration:",
      context_mode: "Switch(config)#",
      config_block: [
        "aaa new-model",
        "aaa authentication dot1x default group radius",
        "interface GigabitEthernet0/2",
        "switchport mode access",
        "dot1x pae supplicant",
        "authentication port-control auto",
        "spanning-tree portfast",
      ],
      error_lines: [
        {
          original: "dot1x pae supplicant",
          corrected: "dot1x pae authenticator",
          explanation:
            "On a switch port the switch acts as the 802.1X authenticator — the device that " +
            "relays EAP between the client (supplicant) and the RADIUS server. 'dot1x pae " +
            "supplicant' would configure the switch to act as a CLIENT. Use 'dot1x pae " +
            "authenticator' paired with 'authentication port-control auto'.",
        },
      ],
      accepted_answers: [
        {
          commands: ["dot1x pae authenticator"],
          explanation:
            "'dot1x pae authenticator' must be configured alongside 'authentication port-control auto' " +
            "to enable the switch port to act as an 802.1X authenticator and exchange EAP frames.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "'authentication port-control auto' alone is insufficient for 802.1X",
        "PAE = Port Access Entity; 'dot1x pae authenticator' sets the role",
      ],
      tags: ["security", "dot1x", "802.1x", "pae", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-sf-015",
      domain: "5.0",
      category: "Security",
      difficulty: 3,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this AAA method-list configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "aaa new-model",
        "aaa authentication login VTY-AUTH group radius enable",
        "aaa authorization exec VTY-AUTH group radius local",
        "line vty 0 4",
        "login authentication VTY-AUTH",
        "authorization exec DEFAULT",
      ],
      error_lines: [
        {
          original: "authorization exec DEFAULT",
          corrected: "authorization exec VTY-AUTH",
          explanation:
            "The authorization method list configured is named 'VTY-AUTH', not 'DEFAULT'. " +
            "Referencing 'DEFAULT' under the line will attempt to use the default method list; " +
            "if no default authorization list is defined the command fails or falls back " +
            "unexpectedly. The line must reference the same list name used in the aaa command.",
        },
      ],
      accepted_answers: [
        {
          commands: ["authorization exec VTY-AUTH"],
          explanation:
            "The method list name on the line must exactly match the name in the 'aaa authorization' command.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "Method list names are case-sensitive and must match exactly",
        "The special name 'default' applies to all lines not explicitly assigned a list",
      ],
      tags: ["security", "aaa", "method-list", "authorization", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ── Automation ───────────────────────────────

    {
      id: "cmd-sf-016",
      domain: "6.0",
      category: "Automation",
      difficulty: 3,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this EEM applet configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "event manager applet LINK-DOWN-ALERT",
        "event syslog pattern 'LINEPROTO-5-UPDOWN'",
        "action 1.0 syslog priority informational msg 'Interface down detected'",
        "action 2.0 mail server 10.0.0.1 to noc@company.com from router@company.com subject 'Link Down'",
      ],
      error_lines: [
        {
          original: "event syslog pattern 'LINEPROTO-5-UPDOWN'",
          corrected: 'event syslog pattern "LINEPROTO-5-UPDOWN"',
          explanation:
            "EEM syslog event patterns must be enclosed in double quotes, not single quotes. " +
            "Using single quotes causes IOS-XE to reject the command with a syntax error.",
        },
      ],
      accepted_answers: [
        {
          commands: ['event syslog pattern "LINEPROTO-5-UPDOWN"'],
          explanation:
            "EEM pattern strings require double quotes on IOS-XE. Single quotes are not valid.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "EEM event/action string arguments always use double quotes in IOS-XE",
        "Single-quoted strings are silently rejected or misinterpreted",
      ],
      tags: ["automation", "eem", "applet", "syslog", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-sf-017",
      domain: "6.0",
      category: "Automation",
      difficulty: 3,
      mode: "syntax_fix",
      prompt:
        "This IP SLA probe is meant to measure basic reachability and round-trip time to 8.8.8.8. " +
        "Find and fix the error so the probe uses the correct operation type:",
      context_mode: "Router(config)#",
      config_block: [
        "ip sla 1",
        "tcp-connect 8.8.8.8 53 source-ip 10.0.0.1 source-port 12000",
        "threshold 2000",
        "timeout 3000",
        "frequency 30",
        "ip sla schedule 1 life forever start-time now",
      ],
      error_lines: [
        {
          original: "tcp-connect 8.8.8.8 53 source-ip 10.0.0.1 source-port 12000",
          corrected: "icmp-echo 8.8.8.8 source-ip 10.0.0.1",
          explanation:
            "For simple reachability and RTT monitoring, 'icmp-echo' is the correct IP SLA " +
            "operation type. 'tcp-connect' tests TCP service availability on a specific port — " +
            "useful for monitoring a real service, but not for a generic reachability probe. " +
            "The 'source-port' parameter is not valid with 'icmp-echo' and should be removed.",
        },
      ],
      accepted_answers: [
        {
          commands: ["icmp-echo 8.8.8.8 source-ip 10.0.0.1"],
          explanation:
            "For basic round-trip latency monitoring use 'icmp-echo'. Reserve 'tcp-connect' " +
            "for testing specific TCP service availability.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "IP SLA operation types: icmp-echo, udp-echo, tcp-connect, udp-jitter, http",
        "Choose the operation type that matches what you are actually measuring",
      ],
      tags: ["automation", "ip-sla", "icmp-echo", "tcp-connect", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-sf-018",
      domain: "6.0",
      category: "Automation",
      difficulty: 3,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this NETCONF/RESTCONF configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "netconf-yang",
        "netconf-yang feature candidate-datastore",
        "restconf",
        "ip http server",
        "ip http secure-server",
        "ip http authentication local",
        "netconf ssh port 22",
      ],
      error_lines: [
        {
          original: "netconf ssh port 22",
          corrected: "netconf-yang ssh port 830",
          explanation:
            "NETCONF operates over SSH on TCP port 830 (RFC 6242), not port 22. " +
            "Port 22 is the standard SSH port used for CLI management. Configuring port 22 " +
            "for NETCONF will either fail or conflict with the management SSH session. " +
            "The correct IOS-XE command syntax is also 'netconf-yang ssh port 830'.",
        },
      ],
      accepted_answers: [
        {
          commands: ["netconf-yang ssh port 830"],
          explanation:
            "NETCONF uses TCP port 830 over SSH. The IOS-XE command is 'netconf-yang ssh port 830'.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "NETCONF: TCP port 830 (RFC 6242)",
        "RESTCONF: TCP port 443 (HTTPS) — standard web port",
        "Do not confuse NETCONF port 830 with SSH port 22",
      ],
      tags: ["automation", "netconf", "yang", "port-830", "syntax-fix"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ═════════════════════════════════════════════════════════════════════
    // EXPANDED QUESTION BANK  (cmd-exp-001 … cmd-exp-056)
    // 30 command_builder + 20 speed_round + 6 syntax_fix = 56 questions
    // ═════════════════════════════════════════════════════════════════════

    // ─────────────────────────────────────────────
    // OSPF  (8 expanded questions)
    // ─────────────────────────────────────────────

    {
      id: "cmd-exp-001",
      domain: "3.0",
      category: "OSPF",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure an OSPF virtual link between Area Border Router R1 (router-id 1.1.1.1) " +
        "and R2 (router-id 2.2.2.2) through transit area 1. Both routers are in OSPF process 1.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "router ospf 1",
            "area 1 virtual-link 2.2.2.2",
          ],
          explanation:
            "A virtual link extends Area 0 connectivity through a non-backbone transit area. " +
            "Both endpoints must specify the other router's OSPF router-ID. " +
            "The transit area number and remote router-ID are required. Configure the command on both ABRs.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "area 0 virtual-link",
          feedback:
            "The area number in 'area <x> virtual-link' is the TRANSIT area (the non-backbone area " +
            "the link passes through), not area 0. Area 0 is the backbone being extended.",
        },
        {
          pattern: "virtual-link 1\\.1\\.1\\.1.*2\\.2\\.2\\.2",
          feedback:
            "Virtual-link syntax is 'area <transit-area> virtual-link <remote-router-id>'. " +
            "Each router specifies only the OTHER router's router-ID.",
        },
      ],
      hints: [
        "Virtual links pass THROUGH a transit area — use the transit area number",
        "Specify the remote ABR's router-ID, not its IP address",
        "Configure on both ABRs",
      ],
      tags: ["ospf", "virtual-link", "area", "abr", "backbone"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-002",
      domain: "3.0",
      category: "OSPF",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "On an ABR running OSPF process 1, configure inter-area summarization. The subnets " +
        "10.1.0.0/16 (10.1.0.0 – 10.1.255.255) reside in area 1 and should be advertised into " +
        "area 0 as a single summary.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["area 1 range 10.1.0.0 255.255.0.0"],
          explanation:
            "'area <area-id> range <network> <mask>' summarizes routes FROM the specified area " +
            "when advertising them into other areas. The area-id is the SOURCE area containing " +
            "the component subnets — in this case area 1. A normal subnet mask (not a wildcard) " +
            "is used. The summary is advertised into all other attached areas, including area 0.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "area.*range.*0\\.0\\.255\\.255",
          feedback:
            "The 'area range' command uses a normal subnet mask (e.g., 255.255.0.0 for /16), " +
            "NOT a wildcard mask. Wildcard masks are only used in 'network' statements.",
        },
        {
          pattern: "summary-address",
          feedback:
            "'summary-address' is used on an ASBR to summarize EXTERNAL (redistributed) routes. " +
            "For inter-area summarization on an ABR, use 'area <id> range'.",
        },
      ],
      hints: [
        "ABR inter-area summary: 'area <source-area> range <network> <subnet-mask>'",
        "Use a normal subnet mask, not a wildcard mask",
        "'summary-address' is for ASBR external route summarization",
      ],
      tags: ["ospf", "summarization", "abr", "area-range", "inter-area"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-003",
      domain: "3.0",
      category: "OSPF",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "On an ASBR running OSPF process 1, summarize external redistributed routes " +
        "10.100.0.0 through 10.100.255.255 (/16) before advertising them into the OSPF domain.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["summary-address 10.100.0.0 255.255.0.0"],
          explanation:
            "ASBRs use 'summary-address' to aggregate external (Type 5/7) LSAs before flooding " +
            "them into OSPF. This differs from ABR inter-area summarization which uses 'area range'. " +
            "The mask here is a subnet mask.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "area.*range",
          feedback:
            "'area range' is ABR inter-area summarization. For EXTERNAL routes on an ASBR, " +
            "use 'summary-address <network> <mask>'.",
        },
        {
          pattern: "summary-address.*0\\.0\\.255\\.255",
          feedback:
            "'summary-address' takes a subnet mask (255.255.0.0 for /16), not a wildcard mask.",
        },
      ],
      hints: [
        "ASBR external summarization uses 'summary-address'",
        "ABR inter-area summarization uses 'area <id> range'",
      ],
      tags: ["ospf", "summarization", "asbr", "summary-address", "external"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-004",
      domain: "3.0",
      category: "OSPF",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Filter OSPF routes using a distribute-list. Apply prefix-list 'OSPF-FILTER' inbound " +
        "to prevent route 192.168.99.0/24 from being installed. You are inside OSPF process 1.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["distribute-list prefix OSPF-FILTER in"],
          explanation:
            "OSPF distribute-lists filter routes from being installed in the RIB but do NOT " +
            "prevent the LSA from being flooded (unlike BGP). The 'in' keyword applies the filter " +
            "to routes being installed locally. 'prefix' references a named prefix-list.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "distribute-list OSPF-FILTER in",
          feedback:
            "When referencing a prefix-list, include the 'prefix' keyword: " +
            "'distribute-list prefix OSPF-FILTER in'. Without 'prefix', IOS-XE expects an ACL name.",
        },
        {
          pattern: "distribute-list.*out",
          feedback:
            "OSPF distribute-list 'out' filters routes being redistributed INTO OSPF, not routes " +
            "being received. For filtering received routes from the RIB, use 'in'.",
        },
      ],
      hints: [
        "OSPF distribute-list filters RIB installation, not LSA flooding",
        "Use 'prefix' keyword to reference a prefix-list",
        "'in' = filter routes going into routing table; 'out' = filter redistribution into OSPF",
      ],
      tags: ["ospf", "distribute-list", "prefix-list", "filtering", "rib"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-005",
      domain: "3.0",
      category: "OSPF",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure OSPF process 1 to originate a default route into the OSPF domain only if " +
        "a default route already exists in the routing table.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["default-information originate"],
          explanation:
            "Without any keywords, 'default-information originate' advertises a Type-5 LSA for " +
            "0.0.0.0/0 only if a default route is present in the local routing table. " +
            "Adding 'always' forces the advertisement regardless of whether a default exists locally.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "default-information originate always",
          feedback:
            "'always' forces the default-route advertisement even when no local default exists — " +
            "potentially causing a black hole. Omit 'always' if you only want to advertise " +
            "when a local default route is present.",
        },
        {
          pattern: "default-route originate",
          feedback:
            "The correct command is 'default-information originate', not 'default-route originate'.",
        },
      ],
      hints: [
        "Without 'always', the default is only advertised if it exists in the routing table",
        "'always' keyword — use carefully: it can create black holes",
      ],
      tags: ["ospf", "default-route", "default-information", "originate"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-006",
      domain: "3.0",
      category: "OSPF",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "What single command, entered inside 'router ospf 1', prevents OSPF hello packets " +
        "from being sent out interface GigabitEthernet0/2 while still advertising the " +
        "connected network?",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["passive-interface GigabitEthernet0/2"],
          explanation:
            "'passive-interface' suppresses OSPF hello packets on the interface, preventing " +
            "neighbor adjacency formation, while still including the connected network in OSPF LSAs. " +
            "Commonly applied to stub LAN interfaces facing end-hosts.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "no ip ospf.*GigabitEthernet",
          feedback:
            "Removing the OSPF network statement would stop advertising the route entirely. " +
            "'passive-interface' keeps the route in OSPF but suppresses hellos.",
        },
      ],
      hints: [
        "The interface stays in OSPF — it just stops sending hellos",
        "Use 'passive-interface default' + 'no passive-interface' for large configs",
      ],
      tags: ["ospf", "passive-interface", "stub", "hello"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-007",
      domain: "3.0",
      category: "OSPF",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Redistribute EIGRP 100 into OSPF process 1 using a route-map called 'EIGRP-TO-OSPF' " +
        "to control which routes are redistributed. Include the 'subnets' keyword.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["redistribute eigrp 100 subnets route-map EIGRP-TO-OSPF"],
          explanation:
            "When redistributing into OSPF, 'subnets' must be included to redistribute classless " +
            "prefixes. The 'route-map' keyword allows selective redistribution and attribute setting. " +
            "Without 'subnets', only classful network boundaries are redistributed.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "redistribute eigrp 100 route-map EIGRP-TO-OSPF$",
          feedback:
            "Missing 'subnets' keyword — without it, only classful networks are redistributed, " +
            "silently dropping all subnet routes.",
        },
        {
          pattern: "redistribute eigrp route-map",
          feedback:
            "The EIGRP AS number is required: 'redistribute eigrp <AS> subnets route-map <name>'.",
        },
      ],
      hints: [
        "Always include 'subnets' when redistributing into OSPF",
        "Route-map goes at the end of the redistribute command",
        "EIGRP AS number is mandatory in the redistribute command",
      ],
      tags: ["ospf", "redistribution", "eigrp", "route-map", "subnets"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-008",
      domain: "3.0",
      category: "OSPF",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "What IOS-XE command displays all OSPF neighbors and their current state on a router?",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: ["show ip ospf neighbor"],
          explanation:
            "'show ip ospf neighbor' lists all OSPF neighbors, their state (e.g., FULL, 2WAY, EXSTART), " +
            "dead time, interface, and neighbor address. Essential for verifying adjacency formation.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show ospf neighbor",
          feedback:
            "The full command is 'show ip ospf neighbor'. The 'ip' keyword is required on IOS-XE.",
        },
      ],
      hints: ["Includes 'ip' in the command", "Shows neighbor state: FULL means fully adjacent"],
      tags: ["ospf", "show", "neighbor", "verification", "adjacency"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    // ─────────────────────────────────────────────
    // EIGRP  (5 expanded questions)
    // ─────────────────────────────────────────────

    {
      id: "cmd-exp-009",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure EIGRP named mode under virtual-instance 'NAMED-MODE' for AS 100 using the " +
        "'address-family ipv4 unicast' model. Set the router-id to 1.1.1.1. (Wide metrics are " +
        "the default in named mode — no extra command needed.)",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "router eigrp NAMED-MODE",
            "address-family ipv4 unicast autonomous-system 100",
            "eigrp router-id 1.1.1.1",
          ],
          explanation:
            "EIGRP named mode uses 'router eigrp <name>' with an address-family sub-mode that " +
            "carries the autonomous-system number. Router-id is set inside the address-family " +
            "with 'eigrp router-id'. Wide (64-bit) metrics are the default in named mode — no " +
            "additional command required to enable them.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "router eigrp 100",
          feedback:
            "EIGRP named mode begins with 'router eigrp <name>' (a string), NOT 'router eigrp 100'. " +
            "The AS number is specified inside the address-family with 'autonomous-system 100'.",
        },
        {
          pattern: "eigrp router-id.*outside",
          feedback:
            "In named mode, 'eigrp router-id' is configured INSIDE the address-family sub-mode, " +
            "not at the top-level router eigrp prompt.",
        },
      ],
      hints: [
        "Named mode: 'router eigrp <name>' then 'address-family ipv4 unicast autonomous-system <AS>'",
        "Router-ID and network statements go inside the address-family",
        "Wide metrics is default in named mode — enables 64-bit metric calculations",
      ],
      tags: ["eigrp", "named-mode", "address-family", "wide-metrics", "router-id"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-010",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Apply a distribute-list using prefix-list 'EIGRP-IN' to filter inbound EIGRP updates " +
        "on interface GigabitEthernet0/0. You are inside EIGRP AS 100 classic mode.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "distribute-list prefix EIGRP-IN in GigabitEthernet0/0",
          ],
          explanation:
            "EIGRP distribute-lists filter routing updates. Using 'prefix' references a named " +
            "prefix-list. Specifying an interface limits the filter to updates received on that interface. " +
            "This prevents unwanted routes from entering the EIGRP topology table.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "distribute-list EIGRP-IN in",
          feedback:
            "Without the 'prefix' keyword, IOS-XE expects an ACL name, not a prefix-list. " +
            "Use 'distribute-list prefix EIGRP-IN in <interface>'.",
        },
      ],
      hints: [
        "'prefix' keyword is needed to reference a named prefix-list",
        "Interface specification makes the filter apply to a specific neighbor direction",
      ],
      tags: ["eigrp", "distribute-list", "prefix-list", "filtering", "inbound"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-011",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure an EIGRP offset-list to add 100000 to the delay metric of all routes " +
        "RECEIVED on interface GigabitEthernet0/1. Use ACL 10 to match all routes. " +
        "You are in EIGRP AS 100.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["offset-list 10 in 100000 GigabitEthernet0/1"],
          explanation:
            "Offset-lists add a value to the EIGRP delay component of the composite metric. " +
            "Syntax: 'offset-list <acl> in|out <offset> [interface]'. " +
            "Higher delay = higher metric = routes less preferred. Useful for influencing path selection.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "offset-list 10 in 100000$",
          feedback:
            "Without the interface, the offset applies to ALL interfaces, not just GigabitEthernet0/1. " +
            "Append the interface name to limit the scope.",
        },
        {
          pattern: "offset-list.*metric",
          feedback:
            "EIGRP offset-list modifies the delay component; there is no 'metric' keyword in " +
            "the offset-list command syntax.",
        },
      ],
      hints: [
        "Syntax: 'offset-list <acl> in|out <value> [interface]'",
        "Offset is added to the delay metric component",
        "Interface is optional but scopes the effect",
      ],
      tags: ["eigrp", "offset-list", "metric", "delay", "path-selection"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-012",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure an EIGRP stub router in AS 100 as 'receive-only' so it accepts routes " +
        "from neighbors but advertises no routes at all (not even connected or summary routes).",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["eigrp stub receive-only"],
          explanation:
            "'eigrp stub receive-only' is the most restrictive stub setting. The router accepts " +
            "EIGRP updates but advertises nothing back. Useful for spoke routers that should " +
            "never be transit paths. Other stub options: connected, summary, static, redistributed.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "eigrp stub$",
          feedback:
            "'eigrp stub' without keywords defaults to advertising connected and summary routes. " +
            "For receive-only behavior, explicitly add the 'receive-only' keyword.",
        },
        {
          pattern: "eigrp stub connected",
          feedback:
            "'eigrp stub connected' still advertises connected routes. " +
            "Use 'receive-only' to prevent ALL advertisement.",
        },
      ],
      hints: [
        "Stub options: connected, summary, static, redistributed, receive-only",
        "'receive-only' is the most restrictive — no routes are advertised",
        "Default 'eigrp stub' = connected + summary",
      ],
      tags: ["eigrp", "stub", "receive-only", "spoke", "hub-spoke"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-013",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "What is the IOS-XE command to display the EIGRP topology table and see all " +
        "feasible successors for AS 100?",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: ["show ip eigrp topology"],
          explanation:
            "'show ip eigrp topology' displays the EIGRP topology table including successor routes " +
            "(best paths) and any feasible successors (backup paths meeting the feasibility condition). " +
            "Add 'all-links' to see all paths including those not meeting the FC.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show eigrp topology",
          feedback:
            "The full command is 'show ip eigrp topology' — the 'ip' keyword is required.",
        },
      ],
      hints: [
        "'show ip eigrp topology all-links' shows paths that don't meet the feasibility condition",
        "Successor = best path; Feasible Successor = backup path meeting FC",
      ],
      tags: ["eigrp", "topology", "show", "feasible-successor", "verification"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    // ─────────────────────────────────────────────
    // BGP  (8 expanded questions)
    // ─────────────────────────────────────────────

    {
      id: "cmd-exp-014",
      domain: "3.0",
      category: "BGP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Set the BGP community value 65001:100 on routes matched by route-map 'SET-COMMUNITY'. " +
        "The route-map is already defined with a match clause. Configure the 'set' action and " +
        "ensure the community is sent to neighbor 203.0.113.1.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "route-map SET-COMMUNITY permit 10",
            "set community 65001:100",
            "router bgp 65001",
            "neighbor 203.0.113.1 send-community",
          ],
          explanation:
            "Communities are set inside a route-map with 'set community <value>'. " +
            "By default BGP does NOT send communities to neighbors — 'send-community' must be " +
            "explicitly configured per neighbor. Without 'send-community', the community attribute " +
            "is stripped before being sent.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "set community 65001:100(?!.*send-community)",
          feedback:
            "Setting the community in a route-map is only half the job. " +
            "You MUST also configure 'neighbor <ip> send-community' or the community will be stripped.",
        },
        {
          pattern: "set community 65001 100",
          feedback:
            "The community value uses a colon as separator: '65001:100', not '65001 100'. " +
            "Without the colon, the values are interpreted differently.",
        },
      ],
      hints: [
        "'set community <AA:NN>' inside the route-map",
        "'neighbor <ip> send-community' is required — communities are NOT sent by default",
        "Extended communities: 'send-community extended' or 'send-community both'",
      ],
      tags: ["bgp", "community", "route-map", "send-community", "policy"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-015",
      domain: "3.0",
      category: "BGP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Route-maps 'ADV-MAP' (matching 192.0.2.0/24) and 'NON-EXIST' (matching 203.0.113.0/24) " +
        "are already defined. Configure BGP conditional advertisement so 192.0.2.0/24 is " +
        "advertised to neighbor 10.1.1.1 ONLY if 203.0.113.0/24 is NOT present in the BGP table. " +
        "You are in router bgp 65001 config mode.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "neighbor 10.1.1.1 advertise-map ADV-MAP non-exist-map NON-EXIST",
          ],
          explanation:
            "BGP conditional advertisement sends routes only under certain conditions. " +
            "'advertise-map' defines what to advertise; 'non-exist-map' triggers advertisement " +
            "when its prefix does NOT exist in the BGP table (backup/failover scenario). " +
            "'exist-map' would trigger when the prefix EXISTS.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "advertise-map.*exist-map(?!.*non)",
          feedback:
            "'exist-map' triggers advertisement when the condition prefix IS present in the BGP table. " +
            "For advertising when the primary is ABSENT, use 'non-exist-map'.",
        },
      ],
      hints: [
        "'non-exist-map' = advertise backup when primary is GONE",
        "'exist-map' = advertise when condition prefix IS present",
        "Both route-maps must be defined separately with match ip address prefix-list statements",
      ],
      tags: ["bgp", "conditional-advertisement", "advertise-map", "non-exist-map", "failover"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-016",
      domain: "3.0",
      category: "BGP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Enable BGP soft reconfiguration inbound for neighbor 203.0.113.1 in AS 65001 so that " +
        "inbound policy changes can be applied without resetting the session.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["neighbor 203.0.113.1 soft-reconfiguration inbound"],
          explanation:
            "'soft-reconfiguration inbound' stores a copy of all received BGP updates before " +
            "applying inbound policy. This allows 'clear ip bgp soft in' to re-apply inbound " +
            "policies without tearing down the session. It consumes additional memory.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "soft-reconfiguration outbound",
          feedback:
            "There is no 'soft-reconfiguration outbound' command — outbound soft reset uses " +
            "Route Refresh capability (RFC 2918) automatically. Only inbound requires explicit config.",
        },
      ],
      hints: [
        "Soft reconfiguration stores a pre-policy copy of received updates",
        "Outbound soft reset uses Route Refresh (no config needed on modern IOS-XE)",
        "Memory intensive — use Route Refresh instead where supported by peer",
      ],
      tags: ["bgp", "soft-reconfiguration", "inbound", "policy", "route-refresh"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-017",
      domain: "3.0",
      category: "BGP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure BGP route dampening with a half-life of 15 minutes, reuse threshold 750, " +
        "suppress threshold 2000, and max-suppress-time of 60 minutes in AS 65001.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["bgp dampening 15 750 2000 60"],
          explanation:
            "BGP dampening penalizes flapping routes to improve stability. Syntax: " +
            "'bgp dampening <half-life> <reuse> <suppress> <max-suppress-time>'. " +
            "Half-life: minutes to halve the penalty; reuse: penalty below which route is unsuppressed; " +
            "suppress: penalty above which route is suppressed; max-suppress-time: max suppression.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "bgp dampening$",
          feedback:
            "'bgp dampening' without parameters uses defaults (half-life 15, reuse 750, " +
            "suppress 2000, max 60). If defaults match, this is acceptable.",
        },
        {
          pattern: "bgp dampening 15 2000 750",
          feedback:
            "Parameter order is: half-life, REUSE, SUPPRESS, max-suppress. " +
            "Reuse (750) must be less than suppress (2000), not the other way around.",
        },
      ],
      hints: [
        "Order: half-life, reuse, suppress, max-suppress-time",
        "Reuse threshold < Suppress threshold",
        "Applied globally or per-neighbor via route-map",
      ],
      tags: ["bgp", "dampening", "route-flap", "stability", "half-life"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-018",
      domain: "3.0",
      category: "BGP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure BGP to aggregate the prefix 10.0.0.0/8 and advertise it to peers, " +
        "suppressing all more-specific routes within that range. AS is 65001.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: ["aggregate-address 10.0.0.0 255.0.0.0 summary-only"],
          explanation:
            "'aggregate-address' creates an aggregated route in the BGP table. " +
            "'summary-only' suppresses all more-specific (component) routes, advertising only the aggregate. " +
            "Without 'summary-only', both the aggregate and the component routes are advertised.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "aggregate-address 10\\.0\\.0\\.0 255\\.0\\.0\\.0$",
          feedback:
            "Without 'summary-only', more-specific routes are also advertised alongside the aggregate. " +
            "Add 'summary-only' to suppress component routes.",
        },
        {
          pattern: "aggregate-address.*0\\.0\\.0\\.255",
          feedback:
            "'aggregate-address' uses a subnet mask (e.g., 255.0.0.0 for /8), not a wildcard mask.",
        },
      ],
      hints: [
        "'summary-only' suppresses more-specific component routes",
        "Uses subnet mask, not wildcard mask",
        "A component route must exist in the BGP table for the aggregate to be generated",
      ],
      tags: ["bgp", "aggregate-address", "summarization", "summary-only", "route-aggregation"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-019",
      domain: "3.0",
      category: "BGP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure BGP maximum-paths for iBGP to 4 (equal-cost multipath) and enable " +
        "load-balancing. You are inside 'router bgp 65001'.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "maximum-paths ibgp 4",
          ],
          explanation:
            "'maximum-paths ibgp <n>' enables BGP ECMP over iBGP paths. Up to 4 equal-cost paths " +
            "can be installed in the routing table simultaneously. For eBGP multipath, use 'maximum-paths <n>'. " +
            "Paths must have the same AS_PATH length and other attributes to qualify as equal-cost.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "maximum-paths 4$",
          feedback:
            "'maximum-paths 4' enables eBGP ECMP, not iBGP. For iBGP load balancing, " +
            "use 'maximum-paths ibgp 4'.",
        },
      ],
      hints: [
        "'maximum-paths ibgp <n>' for iBGP multipath",
        "'maximum-paths <n>' for eBGP multipath",
        "Paths must have identical attributes (AS_PATH length, MED, etc.) to qualify",
      ],
      tags: ["bgp", "maximum-paths", "ibgp", "ecmp", "load-balancing"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-020",
      domain: "3.0",
      category: "BGP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Create a BGP peer-group named 'IBGP-PEERS' in AS 65001, set it to use Loopback0 " +
        "as the update source, set next-hop-self, and add neighbor 10.0.0.2 to it.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "neighbor IBGP-PEERS peer-group",
            "neighbor IBGP-PEERS update-source Loopback0",
            "neighbor IBGP-PEERS next-hop-self",
            "neighbor 10.0.0.2 peer-group IBGP-PEERS",
          ],
          explanation:
            "Peer-groups simplify iBGP configuration by applying shared attributes to a group. " +
            "Create the group, assign attributes (update-source, next-hop-self, etc.), " +
            "then assign individual neighbors to it. Individual neighbors inherit all peer-group settings.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "neighbor 10\\.0\\.0\\.2 peer-group$",
          feedback:
            "'neighbor <ip> peer-group' without a name CREATES a new peer-group named by that IP, " +
            "which is not the intent. Use 'neighbor <ip> peer-group IBGP-PEERS' to assign to existing group.",
        },
      ],
      hints: [
        "First create the peer-group: 'neighbor <name> peer-group'",
        "Then configure attributes on the group",
        "Then assign neighbors: 'neighbor <ip> peer-group <name>'",
      ],
      tags: ["bgp", "peer-group", "ibgp", "scalability", "next-hop-self"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-021",
      domain: "3.0",
      category: "BGP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure a BGP confederation. Local AS is 65100 (the confederation member AS). " +
        "The confederation identifier (external AS) is 65000. " +
        "Add confederation peer 65101 and 65102.",
      context_mode: "Router(config-router)#",
      accepted_answers: [
        {
          commands: [
            "bgp confederation identifier 65000",
            "bgp confederation peers 65101 65102",
          ],
          explanation:
            "BGP confederations split a large AS into sub-ASes (member ASes) to reduce iBGP full-mesh. " +
            "The 'confederation identifier' is the AS number presented to external peers. " +
            "'confederation peers' lists the other member sub-ASes — sessions between them use " +
            "eBGP rules but behave like iBGP for next-hop/local-pref.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "bgp confederation identifier 65100",
          feedback:
            "The confederation identifier is the PUBLIC/EXTERNAL AS (65000), " +
            "not the internal member AS (65100). External peers see 65000.",
        },
        {
          pattern: "bgp confederation peers 65101$",
          feedback:
            "Multiple confederation peers can be listed on one command: " +
            "'bgp confederation peers 65101 65102'.",
        },
      ],
      hints: [
        "Confederation identifier = external AS number visible to outside world",
        "Confederation peers = other internal sub-ASes",
        "Member AS sessions use eBGP mechanics but preserve iBGP attributes",
      ],
      tags: ["bgp", "confederation", "sub-as", "scalability", "ibgp"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    // ─────────────────────────────────────────────
    // STP  (4 expanded questions)
    // ─────────────────────────────────────────────

    {
      id: "cmd-exp-022",
      domain: "2.0",
      category: "STP",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure an MST region on a switch. Region name is 'CAMPUS', revision 1, " +
        "mapping VLANs 1-100 to MST instance 1 and VLANs 101-200 to MST instance 2.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "spanning-tree mode mst",
            "spanning-tree mst configuration",
            "name CAMPUS",
            "revision 1",
            "instance 1 vlan 1-100",
            "instance 2 vlan 101-200",
            "exit",
          ],
          explanation:
            "MST configuration requires entering 'spanning-tree mst configuration' sub-mode. " +
            "The region name and revision number must match on all switches in the same MST region. " +
            "VLAN-to-instance mapping is configured with 'instance <id> vlan <range>'. " +
            "Changes are staged until you exit the sub-mode — the exit is what commits the region.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "spanning-tree mst.*name|revision.*outside",
          feedback:
            "'name' and 'revision' commands are entered INSIDE 'spanning-tree mst configuration' " +
            "sub-mode, not at the global config prompt.",
        },
        {
          pattern: "spanning-tree mode mstp",
          feedback:
            "The keyword is 'mst', not 'mstp': 'spanning-tree mode mst'.",
        },
      ],
      hints: [
        "Enter 'spanning-tree mst configuration' sub-mode first",
        "Name, revision, and instance mappings must match on all region members",
        "Exit sub-mode to apply — changes are pending until you exit",
      ],
      tags: ["stp", "mst", "mstp", "802.1s", "vlan-instance", "region"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-023",
      domain: "2.0",
      category: "STP",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Enable STP Loop Guard globally on a switch and also enable UDLD aggressive mode " +
        "globally to protect against unidirectional link failures.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "spanning-tree loopguard default",
            "udld aggressive",
          ],
          explanation:
            "Loop Guard prevents a non-designated port from transitioning to forwarding if it " +
            "stops receiving BPDUs (puts it in loop-inconsistent state instead). " +
            "UDLD aggressive mode marks unidirectional links as errdisabled. " +
            "Both are configured globally with these commands.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "spanning-tree loop-guard",
          feedback:
            "The correct syntax is 'spanning-tree loopguard default' (no hyphen in 'loopguard').",
        },
        {
          pattern: "udld enable",
          feedback:
            "'udld enable' activates UDLD in normal mode (only sends error message). " +
            "For errdisable on unidirectional links use 'udld aggressive'.",
        },
      ],
      hints: [
        "Loop Guard = 'spanning-tree loopguard default' (one word, no hyphen)",
        "UDLD normal mode logs; UDLD aggressive mode err-disables the port",
        "Both can also be applied per-interface",
      ],
      tags: ["stp", "loop-guard", "udld", "layer2", "unidirectional"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-024",
      domain: "2.0",
      category: "Switching",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure a LACP EtherChannel. Add interfaces GigabitEthernet0/1 and GigabitEthernet0/2 " +
        "to port-channel 1 in active LACP mode. Set the port-channel as a trunk.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "interface range GigabitEthernet0/1 - 2",
            "channel-group 1 mode active",
            "interface Port-channel1",
            "switchport mode trunk",
          ],
          explanation:
            "LACP active mode initiates LACP negotiation. 'channel-group 1 mode active' assigns " +
            "interfaces to the port-channel. The logical Port-channel interface is then configured " +
            "as a trunk. LACP modes: active (initiates), passive (responds only), on (no negotiation).",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "channel-group 1 mode passive.*passive",
          feedback:
            "Two LACP passive interfaces will NOT form an EtherChannel — at least one side must " +
            "be active to initiate LACP negotiation.",
        },
        {
          pattern: "channel-group 1 mode desirable|auto",
          feedback:
            "'desirable' and 'auto' are PAgP modes, not LACP modes. " +
            "LACP uses 'active' (initiates) or 'passive' (responds).",
        },
      ],
      hints: [
        "LACP modes: active (initiates), passive (waits) — both ends passive = no channel",
        "PAgP modes: desirable / auto",
        "Configure the Port-channel interface for trunk/access settings",
      ],
      tags: ["stp", "etherchannel", "lacp", "port-channel", "trunk", "802.3ad"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-025",
      domain: "2.0",
      category: "Switching",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "What command verifies the EtherChannel bundle status, showing whether member ports " +
        "are bundled (P), suspended (s), or in hot-standby (H)?",
      context_mode: "Switch#",
      accepted_answers: [
        {
          commands: ["show etherchannel summary"],
          explanation:
            "'show etherchannel summary' displays each port-channel, its protocol (LACP/PAgP/Static), " +
            "member interfaces, and their status flags: P=bundled, s=suspended, I=stand-alone, " +
            "H=hot-standby, D=down. Essential for verifying EtherChannel formation.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show port-channel",
          feedback:
            "'show port-channel' may not be available on all platforms. " +
            "'show etherchannel summary' is the standard IOS-XE verification command.",
        },
      ],
      hints: [
        "Flag 'P' = port is bundled and active in the channel",
        "Flag 'D' = port is down",
        "'show etherchannel detail' gives verbose per-port information",
      ],
      tags: ["stp", "etherchannel", "lacp", "pagp", "show", "verification"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    // ─────────────────────────────────────────────
    // Security  (8 expanded questions)
    // ─────────────────────────────────────────────

    {
      id: "cmd-exp-026",
      domain: "5.0",
      category: "Security",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure an IPsec crypto map named 'VPN-MAP' sequence 10 on a router. " +
        "Match ACL 100, set peer to 203.0.113.5, use transform-set 'TS-AES', " +
        "and apply the crypto map to GigabitEthernet0/0.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "crypto map VPN-MAP 10 ipsec-isakmp",
            "match address 100",
            "set peer 203.0.113.5",
            "set transform-set TS-AES",
            "interface GigabitEthernet0/0",
            "crypto map VPN-MAP",
          ],
          explanation:
            "A crypto map defines IPsec policy: which traffic to encrypt (match address), " +
            "where to send it (set peer), and how to encrypt it (set transform-set). " +
            "'ipsec-isakmp' means IKE is used for key exchange. The map must be applied to " +
            "an interface to take effect.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "crypto map VPN-MAP 10$",
          feedback:
            "The 'ipsec-isakmp' keyword is required: 'crypto map VPN-MAP 10 ipsec-isakmp'. " +
            "Without it, the crypto map type is undefined.",
        },
        {
          pattern: "match address.*(?!100)",
          feedback:
            "'match address' references an ACL number or name that defines interesting traffic " +
            "to be encrypted. Ensure the ACL exists and correctly identifies the VPN traffic.",
        },
      ],
      hints: [
        "Syntax: 'crypto map <name> <seq> ipsec-isakmp'",
        "Three required sub-commands: match address, set peer, set transform-set",
        "Apply to interface with 'crypto map <name>'",
      ],
      tags: ["security", "ipsec", "crypto-map", "vpn", "isakmp", "transform-set"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-027",
      domain: "5.0",
      category: "Security",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure a Zone-Based Firewall (ZBF) with two zones: 'INSIDE' and 'OUTSIDE'. " +
        "Assign GigabitEthernet0/0 to INSIDE and GigabitEthernet0/1 to OUTSIDE. " +
        "Create a zone-pair allowing INSIDE to OUTSIDE traffic inspected by policy 'FW-POLICY'.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "zone security INSIDE",
            "zone security OUTSIDE",
            "interface GigabitEthernet0/0",
            "zone-member security INSIDE",
            "interface GigabitEthernet0/1",
            "zone-member security OUTSIDE",
            "zone-pair security IN-TO-OUT source INSIDE destination OUTSIDE",
            "service-policy type inspect FW-POLICY",
          ],
          explanation:
            "ZBF requires: define zones, assign interfaces, create zone-pairs, apply policy. " +
            "Traffic between zones is denied by default unless a zone-pair with a policy is configured. " +
            "Intra-zone traffic is allowed by default. The self zone represents the router itself.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "zone-pair.*source OUTSIDE destination INSIDE",
          feedback:
            "Zone-pairs are unidirectional. 'source INSIDE destination OUTSIDE' allows inside-initiated " +
            "traffic. A separate zone-pair is needed for return traffic unless stateful inspection handles it.",
        },
        {
          pattern: "ip inspect",
          feedback:
            "'ip inspect' is the legacy CBAC firewall. Zone-Based Firewall (ZBF) uses " +
            "'zone security', 'zone-member security', and 'zone-pair security' commands.",
        },
      ],
      hints: [
        "ZBF flow: define zones → assign interfaces → zone-pairs → apply policy",
        "Default inter-zone policy = DENY ALL",
        "Use 'inspect' action for stateful inspection; 'pass' for stateless",
      ],
      tags: ["security", "zbf", "zone-based-firewall", "zone-pair", "inspect", "policy"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-028",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Enable DHCP snooping for VLANs 10 and 20 on a switch. Mark GigabitEthernet1/0/24 " +
        "(uplink to DHCP server) as a trusted port. Limit untrusted ports to 10 DHCP packets per second.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "ip dhcp snooping",
            "ip dhcp snooping vlan 10,20",
            "interface GigabitEthernet1/0/24",
            "ip dhcp snooping trust",
            "interface range GigabitEthernet1/0/1 - 23",
            "ip dhcp snooping limit rate 10",
          ],
          explanation:
            "DHCP snooping filters rogue DHCP servers. Trusted ports forward DHCP offers/acks; " +
            "untrusted ports only forward client requests. Rate limiting on untrusted ports " +
            "protects against DHCP starvation attacks. The global 'ip dhcp snooping' must be " +
            "enabled along with per-VLAN activation.",
          is_primary: true,
        },
        {
          commands: [
            "ip dhcp snooping",
            "ip dhcp snooping vlan 10,20",
            "interface GigabitEthernet1/0/24",
            "ip dhcp snooping trust",
          ],
          explanation:
            "Minimum configuration without rate limiting on untrusted ports.",
          is_primary: false,
        },
      ],
      common_mistakes: [
        {
          pattern: "ip dhcp snooping vlan 10 20",
          feedback:
            "Use a comma to list multiple VLANs: 'ip dhcp snooping vlan 10,20' " +
            "or use a range: 'ip dhcp snooping vlan 10-20'.",
        },
        {
          pattern: "^ip dhcp snooping vlan",
          feedback:
            "The global 'ip dhcp snooping' command (without vlan) must be enabled first " +
            "before per-VLAN activation takes effect.",
        },
      ],
      hints: [
        "Two-step: global enable then per-VLAN enable",
        "Trust uplinks to DHCP servers/switches, not access ports",
        "Rate limiting protects against DHCP exhaustion on untrusted ports",
      ],
      tags: ["security", "dhcp-snooping", "layer2", "trusted-port", "rate-limit"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-029",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Enable IP Source Guard on an untrusted access port GigabitEthernet0/5 to validate " +
        "that source IP addresses match the DHCP snooping binding table.",
      context_mode: "Switch(config-if)#",
      accepted_answers: [
        {
          commands: ["ip verify source"],
          explanation:
            "IP Source Guard (ip verify source) validates source IP addresses against the " +
            "DHCP snooping binding table. Frames with unverified source IPs are dropped. " +
            "Optionally 'ip verify source port-security' also validates MAC addresses. " +
            "DHCP snooping must be enabled first.",
          is_primary: true,
        },
        {
          commands: ["ip verify source port-security"],
          explanation:
            "'ip verify source port-security' validates both IP and MAC address against the " +
            "DHCP snooping binding table and port-security MAC table.",
          is_primary: false,
        },
      ],
      common_mistakes: [
        {
          pattern: "ip source-guard",
          feedback:
            "The IOS-XE command is 'ip verify source', not 'ip source-guard'. " +
            "Some documentation uses 'IP Source Guard' as the feature name.",
        },
      ],
      hints: [
        "Requires DHCP snooping to be enabled and the binding table populated",
        "'ip verify source' = IP check only",
        "'ip verify source port-security' = IP + MAC check",
      ],
      tags: ["security", "ip-source-guard", "ip-verify-source", "dhcp-snooping", "layer2"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-030",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure port-security on interface GigabitEthernet0/3. Allow a maximum of 2 MAC " +
        "addresses, enable sticky learning, and set the violation action to restrict.",
      context_mode: "Switch(config-if)#",
      accepted_answers: [
        {
          commands: [
            "switchport mode access",
            "switchport port-security",
            "switchport port-security maximum 2",
            "switchport port-security mac-address sticky",
            "switchport port-security violation restrict",
          ],
          explanation:
            "Port-security limits MAC addresses on an access port. 'Sticky' dynamically learns " +
            "and saves MACs to the running config. Violation modes: protect (drop, no log), " +
            "restrict (drop + log + increment counter), shutdown (err-disable the port).",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "switchport port-security violation shutdown",
          feedback:
            "'shutdown' err-disables the entire port on violation. The question asks for 'restrict' " +
            "which drops frames and logs but keeps the port up.",
        },
        {
          pattern: "port-security mac-address sticky(?!.*maximum)",
          feedback:
            "Sticky alone sets max to 1 by default. Explicitly set 'maximum 2' to allow 2 addresses.",
        },
      ],
      hints: [
        "Port must be in access mode before port-security can be applied",
        "Violation modes: protect (silent drop), restrict (drop+log), shutdown (err-disable)",
        "'sticky' dynamically learns MACs and saves them to running config",
      ],
      tags: ["security", "port-security", "sticky", "violation", "layer2", "mac"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-031",
      domain: "5.0",
      category: "Security",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure Control Plane Policing (CoPP) to rate-limit ICMP traffic destined to " +
        "the router's control plane to 1000 packets per second. " +
        "Use class-map 'ICMP-CLASS', policy-map 'COPP-POLICY', and apply to the control-plane.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "class-map match-all ICMP-CLASS",
            "match protocol icmp",
            "policy-map COPP-POLICY",
            "class ICMP-CLASS",
            "police rate 1000 pps",
            "control-plane",
            "service-policy input COPP-POLICY",
          ],
          explanation:
            "CoPP protects the router CPU by rate-limiting traffic to the control plane. " +
            "A class-map classifies traffic, a policy-map applies policing, and the policy " +
            "is applied under 'control-plane' with 'service-policy input'. " +
            "CoPP only has an 'input' direction (traffic destined to the router).",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "service-policy output",
          feedback:
            "CoPP only supports 'service-policy input' under the control-plane. " +
            "There is no output policy for the control plane.",
        },
        {
          pattern: "interface.*service-policy",
          feedback:
            "CoPP policy is applied under 'control-plane' configuration mode, not under an interface.",
        },
      ],
      hints: [
        "Apply policy under 'control-plane' sub-mode, not an interface",
        "CoPP is INPUT only — no output direction",
        "Police rate can be in bps or pps; use 'pps' for packet rate limiting",
      ],
      tags: ["security", "copp", "control-plane", "policing", "qos", "cpu-protection"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-032",
      domain: "5.0",
      category: "Security",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "What IOS-XE show command displays the Control Plane Policing statistics including " +
        "conforming and exceeding packet counts?",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: ["show policy-map control-plane"],
          explanation:
            "'show policy-map control-plane' displays the CoPP policy applied to the control-plane " +
            "along with class statistics: offered rate, conformed packets, exceeded packets, " +
            "and dropped packets. Essential for verifying CoPP is working and tuning thresholds.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show control-plane policy",
          feedback:
            "The correct syntax is 'show policy-map control-plane', not 'show control-plane policy'.",
        },
      ],
      hints: ["Mirrors 'show policy-map interface' syntax but for the control-plane"],
      tags: ["security", "copp", "control-plane", "show", "verification", "policing"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-033",
      domain: "5.0",
      category: "Security",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "What IOS-XE command on a switch displays all DHCP snooping binding table entries " +
        "showing MAC address, IP address, VLAN, interface, and lease time?",
      context_mode: "Switch#",
      accepted_answers: [
        {
          commands: ["show ip dhcp snooping binding"],
          explanation:
            "'show ip dhcp snooping binding' displays the DHCP snooping database with entries " +
            "learned from DHCP transactions on untrusted ports. This table is also used by " +
            "Dynamic ARP Inspection and IP Source Guard for validation.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show dhcp snooping",
          feedback:
            "The correct command is 'show ip dhcp snooping binding' — the 'ip' keyword is required.",
        },
      ],
      hints: [
        "Binding table entries are populated by DHCP ACK packets on untrusted ports",
        "Used by DAI and IP Source Guard as the authoritative MAC-to-IP mapping",
      ],
      tags: ["security", "dhcp-snooping", "binding-table", "show", "verification"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    // ─────────────────────────────────────────────
    // Network Assurance  (5 expanded questions)
    // ─────────────────────────────────────────────

    {
      id: "cmd-exp-034",
      domain: "6.0",
      category: "Network Assurance",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure Flexible NetFlow. Create a flow record 'MY-RECORD' matching IPv4 source/dest " +
        "address and protocol, collecting bytes/packets. Create an exporter 'MY-EXPORTER' sending " +
        "to 10.0.0.100 UDP 2055. Create a monitor 'MY-MONITOR' and apply it to GigabitEthernet0/0 input.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "flow record MY-RECORD",
            "match ipv4 source address",
            "match ipv4 destination address",
            "match ipv4 protocol",
            "collect counter bytes",
            "collect counter packets",
            "flow exporter MY-EXPORTER",
            "destination 10.0.0.100",
            "transport udp 2055",
            "flow monitor MY-MONITOR",
            "record MY-RECORD",
            "exporter MY-EXPORTER",
            "interface GigabitEthernet0/0",
            "ip flow monitor MY-MONITOR input",
          ],
          explanation:
            "Flexible NetFlow has three components: flow record (what to capture), " +
            "flow exporter (where to send data), and flow monitor (binds record + exporter, " +
            "applied to interface). UDP 2055 is the standard NetFlow collector port.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ip flow-export destination",
          feedback:
            "'ip flow-export' is legacy NetFlow v5/v9. Flexible NetFlow uses 'flow exporter', " +
            "'flow record', and 'flow monitor' configuration blocks.",
        },
        {
          pattern: "ip flow ingress",
          feedback:
            "Legacy command. Flexible NetFlow uses 'ip flow monitor <name> input|output' " +
            "under the interface.",
        },
      ],
      hints: [
        "Three building blocks: flow record + flow exporter + flow monitor",
        "Apply monitor to interface: 'ip flow monitor <name> input|output'",
        "UDP 2055 is default NetFlow collector port; 4739 is IPFIX",
      ],
      tags: ["network-assurance", "netflow", "flexible-netflow", "flow-monitor", "telemetry"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-035",
      domain: "6.0",
      category: "Network Assurance",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure IP SLA 1 as an ICMP-echo probe to 8.8.8.8 sourced from 10.0.0.1, " +
        "running every 10 seconds. Create IP SLA tracking object 1 that tracks reachability " +
        "and configure a static default route via 203.0.113.1 that is removed if track 1 goes down.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "ip sla 1",
            "icmp-echo 8.8.8.8 source-ip 10.0.0.1",
            "frequency 10",
            "ip sla schedule 1 life forever start-time now",
            "track 1 ip sla 1 reachability",
            "ip route 0.0.0.0 0.0.0.0 203.0.113.1 track 1",
          ],
          explanation:
            "IP SLA probes test network conditions. Combined with object tracking, they enable " +
            "reliable static routing. 'track 1 ip sla 1 reachability' creates a tracking object " +
            "that is up when the IP SLA probe succeeds. The tracked static route is removed from " +
            "the RIB when the track goes down, triggering failover.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ip sla schedule.*(?!start-time now)",
          feedback:
            "Without 'start-time now', the IP SLA probe does not start. " +
            "Always include 'start-time now' or a specific start time.",
        },
        {
          pattern: "track.*ip route",
          feedback:
            "The tracking object tracks the IP SLA probe state ('track 1 ip sla 1 reachability'), " +
            "not a route. The static route then references the tracking object with 'track 1'.",
        },
      ],
      hints: [
        "Must schedule the SLA: 'ip sla schedule <id> life forever start-time now'",
        "Track object: 'track <id> ip sla <sla-id> reachability'",
        "Static route: 'ip route ... track <track-id>'",
      ],
      tags: ["network-assurance", "ip-sla", "tracking", "static-route", "icmp-echo", "failover"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-036",
      domain: "6.0",
      category: "Network Assurance",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure a SPAN session (session 1) to monitor all traffic on source port " +
        "GigabitEthernet0/1 (both Rx and Tx) and send it to destination port GigabitEthernet0/10.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "monitor session 1 source interface GigabitEthernet0/1 both",
            "monitor session 1 destination interface GigabitEthernet0/10",
          ],
          explanation:
            "SPAN (Switched Port Analyzer) mirrors traffic from source ports/VLANs to a destination port " +
            "for analysis by a packet capture device. 'both' captures ingress and egress traffic. " +
            "The destination port is taken out of normal switching and dedicated to the SPAN session.",
          is_primary: true,
        },
        {
          commands: [
            "monitor session 1 source interface GigabitEthernet0/1",
            "monitor session 1 destination interface GigabitEthernet0/10",
          ],
          explanation:
            "Without 'both', the default captures both directions on most platforms.",
          is_primary: false,
        },
      ],
      common_mistakes: [
        {
          pattern: "span session",
          feedback:
            "The IOS-XE command is 'monitor session', not 'span session'.",
        },
        {
          pattern: "monitor session 1 destination interface GigabitEthernet0/1",
          feedback:
            "Do not use the monitored source port as the destination — this creates a loop. " +
            "The destination must be a different, dedicated port.",
        },
      ],
      hints: [
        "IOS-XE uses 'monitor session', not 'span'",
        "Direction keywords: rx (ingress only), tx (egress only), both (default on most platforms)",
        "Destination port is taken out of normal operation",
      ],
      tags: ["network-assurance", "span", "mirror", "monitor-session", "packet-capture"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-037",
      domain: "6.0",
      category: "Network Assurance",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure SNMPv3 with a user 'NMS-USER' in group 'NMS-GROUP'. " +
        "Use auth SHA with password 'AuthPass1!' and privacy AES 128 with password 'PrivPass1!'. " +
        "Set the SNMP server host to 10.0.0.200 using this user.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "snmp-server group NMS-GROUP v3 priv",
            "snmp-server user NMS-USER NMS-GROUP v3 auth sha AuthPass1! priv aes 128 PrivPass1!",
            "snmp-server host 10.0.0.200 version 3 priv NMS-USER",
          ],
          explanation:
            "SNMPv3 provides authentication and encryption. First create the group with security level " +
            "'priv' (authentication + privacy). Then create the user specifying auth (SHA/MD5) " +
            "and priv (AES/DES) passwords. Finally specify the trap destination. " +
            "Security levels: noauth, auth (auth only), priv (auth + encryption).",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "snmp-server user.*v3 auth md5",
          feedback:
            "MD5 is deprecated for SNMP. Use SHA for authentication in modern deployments.",
        },
        {
          pattern: "snmp-server host.*version 3 auth",
          feedback:
            "When using privacy (encryption), the host command security level must be 'priv', " +
            "not 'auth'. Match the security level used in the group definition.",
        },
      ],
      hints: [
        "Security levels: noauth < auth < priv",
        "Group security level must be equal to or higher than user security level",
        "Order: group → user → host",
      ],
      tags: ["network-assurance", "snmp", "snmpv3", "auth", "priv", "aes", "sha"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-038",
      domain: "6.0",
      category: "Network Assurance",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "What IOS-XE command shows all configured IP SLA operations, their type, " +
        "and current status (active/inactive)?",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: ["show ip sla summary"],
          explanation:
            "'show ip sla summary' provides a concise overview of all IP SLA operations: " +
            "ID, type (icmp-echo, udp-jitter, etc.), destination, current state, and return code. " +
            "Use 'show ip sla statistics' for detailed RTT and success/failure counters.",
          is_primary: true,
        },
        {
          commands: ["show ip sla statistics"],
          explanation:
            "'show ip sla statistics' shows detailed performance data per SLA including RTT, " +
            "jitter, packet loss, and MOS scores for voice-quality SLAs.",
          is_primary: false,
        },
      ],
      common_mistakes: [
        {
          pattern: "show ip sla monitor",
          feedback:
            "'show ip sla monitor' is an older IOS syntax. Current IOS-XE uses 'show ip sla summary'.",
        },
      ],
      hints: [
        "'show ip sla summary' = concise status overview",
        "'show ip sla statistics' = detailed RTT/jitter/loss data",
      ],
      tags: ["network-assurance", "ip-sla", "show", "verification", "monitoring"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    // ─────────────────────────────────────────────
    // Automation  (6 expanded questions)
    // ─────────────────────────────────────────────

    {
      id: "cmd-exp-039",
      domain: "6.0",
      category: "Automation",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure an EEM applet named 'TRACK-SYSLOG' that triggers when a syslog message " +
        "matching the pattern 'OSPF-5-ADJCHG' is seen. The action should send a syslog message " +
        "with priority informational saying 'OSPF adjacency changed — alerting NMS'.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "event manager applet TRACK-SYSLOG",
            'event syslog pattern "OSPF-5-ADJCHG"',
            'action 1.0 syslog priority informational msg "OSPF adjacency changed -- alerting NMS"',
          ],
          explanation:
            "EEM applets automate responses to network events. The 'event syslog pattern' trigger " +
            "uses a regex pattern in double quotes to match syslog messages. " +
            "The 'action syslog' sends a custom syslog message. Action numbering allows multiple " +
            "sequential actions (1.0, 2.0, etc.).",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "event syslog pattern 'OSPF-5-ADJCHG'",
          feedback:
            "EEM pattern strings require DOUBLE quotes, not single quotes. " +
            "Single-quoted strings are rejected by IOS-XE.",
        },
        {
          pattern: "action.*log(?! priority)",
          feedback:
            "The correct EEM syslog action is 'action <num> syslog priority <level> msg <text>'. " +
            "The 'priority' and 'msg' keywords are required.",
        },
      ],
      hints: [
        "Pattern strings always use double quotes in EEM",
        "Action format: 'action <num> syslog priority <level> msg \"<text>\"'",
        "Multiple actions use sequential numbers: 1.0, 2.0, 3.0",
      ],
      tags: ["automation", "eem", "applet", "syslog-trigger", "event-manager"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-040",
      domain: "6.0",
      category: "Automation",
      difficulty: 2,
      mode: "speed_round",
      prompt:
        "In the Python 'requests' library, what keyword argument (in the form 'name=value') " +
        "disables SSL certificate verification on a GET or POST call?",
      context_mode: null,
      accepted_answers: [
        {
          commands: [
            "verify=False",
          ],
          explanation:
            "Passing verify=False to requests.get()/post()/etc. skips TLS certificate validation. " +
            "Acceptable in labs and during initial DNA Center / Catalyst Center integration; " +
            "production code should supply a proper CA bundle via 'verify=\"/path/to/ca.pem\"'.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "requests.get.*headers.*Authorization",
          feedback:
            "While using 'Authorization' headers is valid, the simpler 'auth=()' tuple shorthand " +
            "for Basic Auth is the standard approach with the requests library.",
        },
      ],
      hints: [
        "requests.get(url, auth=(user, pass), verify=False)",
        "DNA Center usually requires X-Auth-Token after the initial /auth/token POST",
        "verify=False disables SSL cert check — only for labs",
      ],
      tags: ["automation", "python", "requests", "dna-center", "api", "rest"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-041",
      domain: "6.0",
      category: "Automation",
      difficulty: 2,
      mode: "speed_round",
      prompt:
        "What HTTP Accept header value does a RESTCONF client send to request JSON-encoded " +
        "YANG data from an IOS-XE device?",
      context_mode: null,
      accepted_answers: [
        {
          commands: [
            "application/yang-data+json",
          ],
          explanation:
            "RESTCONF (RFC 8040) uses the media type 'application/yang-data+json' for JSON " +
            "payloads and 'application/yang-data+xml' for XML. Sent as the Accept header on GET " +
            "requests and as Content-Type on PUT/POST/PATCH. RESTCONF always runs over HTTPS.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "http://.*restconf",
          feedback:
            "RESTCONF uses HTTPS (port 443), not HTTP. IOS-XE requires 'ip http secure-server' " +
            "to be enabled for RESTCONF.",
        },
        {
          pattern: "Accept.*application/json",
          feedback:
            "RESTCONF requires the specific MIME type 'application/yang-data+json', " +
            "not the generic 'application/json'.",
        },
      ],
      hints: [
        "RESTCONF base URI: /restconf/data/<module>:<container>",
        "Accept header: application/yang-data+json",
        "Content-Type header (for PUT/POST/PATCH): application/yang-data+json",
      ],
      tags: ["automation", "restconf", "curl", "yang", "json", "api"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-042",
      domain: "6.0",
      category: "Automation",
      difficulty: 2,
      mode: "speed_round",
      prompt:
        "What is the fully-qualified Ansible module name (collection.name) that pushes raw IOS " +
        "configuration lines to a Cisco IOS-XE device?",
      context_mode: null,
      accepted_answers: [
        {
          commands: [
            "cisco.ios.ios_config",
          ],
          explanation:
            "cisco.ios.ios_config sends raw CLI configuration lines (via the 'lines' parameter) " +
            "to IOS/IOS-XE devices. For commands that need a parent context (e.g., under an " +
            "interface), use 'parents'. For structured fact-gathering use cisco.ios.ios_facts; " +
            "for show commands use cisco.ios.ios_command.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ios_command",
          feedback:
            "'ios_command' sends show/exec commands and returns output — it does NOT configure the device. " +
            "Use 'ios_config' for configuration changes.",
        },
      ],
      hints: [
        "'ios_config' for configuration, 'ios_command' for show commands",
        "Use 'parents' for commands that need a context (e.g., interface GigabitEthernet0/0)",
        "Always use FQCN: cisco.ios.ios_config",
      ],
      tags: ["automation", "ansible", "ios_config", "playbook", "network-programmability"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-043",
      domain: "6.0",
      category: "Automation",
      difficulty: 3,
      mode: "speed_round",
      prompt:
        "What NETCONF RPC operation modifies configuration on a target datastore (the equivalent " +
        "of PUT/PATCH in RESTCONF)?",
      context_mode: null,
      accepted_answers: [
        {
          commands: ["edit-config"],
          explanation:
            "'edit-config' is the NETCONF RPC that writes to a target datastore (running, " +
            "candidate, or startup). Per-element operation attributes (merge — the default — " +
            "plus replace, create, delete, remove) control how individual nodes are changed. " +
            "<get-config> reads config; <get> reads both config and state data.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "set-config",
          feedback:
            "There is no 'set-config' RPC in NETCONF. The correct operation is 'edit-config'.",
        },
        {
          pattern: "edit-config.*startup",
          feedback:
            "Targeting <startup/> modifies the startup config. Most operations target <running/> " +
            "for immediate effect, or <candidate/> if candidate datastore is supported.",
        },
      ],
      hints: [
        "NETCONF RPCs: get, get-config, edit-config, copy-config, delete-config, lock, unlock",
        "edit-config targets a datastore and contains a <config> payload",
        "operation attribute values: merge (default), replace, create, delete, remove",
      ],
      tags: ["automation", "netconf", "edit-config", "rpc", "xml", "yang"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-044",
      domain: "6.0",
      category: "Automation",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "What IOS-XE command enables an IP SLA responder on a device so that it can " +
        "respond to UDP jitter and TCP connect probes from an IP SLA sender?",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: ["ip sla responder"],
          explanation:
            "'ip sla responder' enables the global IP SLA responder, allowing the device to " +
            "respond to IP SLA probes. For UDP jitter probes, the responder must be running on " +
            "the target device. For ICMP echo, a responder is not required.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ip sla respond",
          feedback:
            "The correct command is 'ip sla responder' (full word), not 'ip sla respond'.",
        },
      ],
      hints: [
        "Required for UDP jitter and TCP connect probes",
        "NOT required for ICMP echo probes",
        "Also configure: 'ip sla responder udp-echo ipaddress <ip> port <port>' for specific probes",
      ],
      tags: ["automation", "ip-sla", "responder", "udp-jitter", "probes"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    // ─────────────────────────────────────────────
    // Virtualization  (4 expanded questions)
    // ─────────────────────────────────────────────

    {
      id: "cmd-exp-049",
      domain: "1.0",
      category: "Virtualization",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure VRF 'CUSTOMER-A' with route distinguisher 65001:100 and route-targets " +
        "65001:100 for both import and export. Assign interface GigabitEthernet0/1 to this VRF.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "vrf definition CUSTOMER-A",
            "rd 65001:100",
            "address-family ipv4",
            "route-target export 65001:100",
            "route-target import 65001:100",
            "interface GigabitEthernet0/1",
            "vrf forwarding CUSTOMER-A",
            "ip address 192.168.1.1 255.255.255.0",
          ],
          explanation:
            "VRF-Lite uses 'vrf definition' (modern IOS-XE). The RD makes prefixes unique in MP-BGP. " +
            "Route-targets control VPN route import/export. " +
            "'vrf forwarding' assigns the interface to the VRF — note this removes any existing " +
            "IP address from the interface, so the address must be re-applied.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ip vrf CUSTOMER-A",
          feedback:
            "'ip vrf' is legacy IOS syntax. Modern IOS-XE uses 'vrf definition' which supports " +
            "both IPv4 and IPv6 address families.",
        },
        {
          pattern: "ip vrf forwarding",
          feedback:
            "Modern IOS-XE uses 'vrf forwarding <name>' under the interface, not 'ip vrf forwarding'.",
        },
      ],
      hints: [
        "Modern IOS-XE: 'vrf definition' (not 'ip vrf')",
        "Route-targets are configured inside 'address-family ipv4' sub-mode",
        "'vrf forwarding' on interface removes existing IP — re-apply the address",
      ],
      tags: ["virtualization", "vrf", "vrf-lite", "rd", "route-target", "mpls-vpn"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-050",
      domain: "1.0",
      category: "Virtualization",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure a GRE tunnel (Tunnel0) between 192.168.1.1 (local) and 203.0.113.5 (remote). " +
        "Assign the tunnel IP 10.0.0.1/30. Protect the tunnel with IPsec profile 'IPSEC-PROFILE'.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "interface Tunnel0",
            "ip address 10.0.0.1 255.255.255.252",
            "tunnel source 192.168.1.1",
            "tunnel destination 203.0.113.5",
            "tunnel mode gre ip",
            "tunnel protection ipsec profile IPSEC-PROFILE",
          ],
          explanation:
            "GRE tunnels encapsulate traffic between endpoints. Adding 'tunnel protection ipsec profile' " +
            "encrypts the GRE payload with IPsec. The IPsec profile must be pre-configured with " +
            "IKE and transform policies. 'tunnel mode gre ip' is the default but should be explicit.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "crypto map.*Tunnel",
          feedback:
            "For GRE over IPsec, use 'tunnel protection ipsec profile' on the tunnel interface, " +
            "not a crypto map. Crypto maps are applied to physical interfaces for traditional IPsec.",
        },
      ],
      hints: [
        "'tunnel protection ipsec profile <name>' secures the GRE tunnel with IPsec",
        "IPsec profile must exist with 'crypto ipsec profile <name>'",
        "GRE mode is default but specify 'tunnel mode gre ip' for clarity",
      ],
      tags: ["virtualization", "gre", "tunnel", "ipsec", "overlay", "vpn"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-051",
      domain: "1.0",
      category: "Virtualization",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure a DMVPN hub router on Tunnel0 using mGRE. Tunnel source GigabitEthernet0/0, " +
        "tunnel IP 10.10.10.1/24, NHRP network-id 1, NHRP authentication 'DMVPN-AUTH', and " +
        "enable dynamic multicast mapping so spokes can register and receive routing updates. " +
        "(The hub IS the NHRP NHS by definition — spokes point 'ip nhrp nhs' at this router.)",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "interface Tunnel0",
            "ip address 10.10.10.1 255.255.255.0",
            "tunnel source GigabitEthernet0/0",
            "tunnel mode gre multipoint",
            "ip nhrp network-id 1",
            "ip nhrp authentication DMVPN-AUTH",
            "ip nhrp map multicast dynamic",
          ],
          explanation:
            "DMVPN hub uses mGRE (multipoint GRE) to support multiple dynamic spoke tunnels. " +
            "NHRP provides address resolution for spoke-to-spoke communication. " +
            "'ip nhrp map multicast dynamic' allows the hub to forward multicast/routing protocol " +
            "traffic to all registered spokes dynamically.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "tunnel mode gre ip(?! multipoint)",
          feedback:
            "DMVPN hub requires 'tunnel mode gre multipoint' (mGRE), not the point-to-point " +
            "'tunnel mode gre ip'. mGRE allows a single tunnel interface to connect to multiple spokes.",
        },
        {
          pattern: "ip nhrp nhs",
          feedback:
            "The hub does not configure 'ip nhrp nhs' — that is configured on SPOKES to point to " +
            "the hub's tunnel IP. The hub uses 'ip nhrp map multicast dynamic'.",
        },
      ],
      hints: [
        "Hub: mGRE + 'ip nhrp map multicast dynamic'",
        "Spoke: 'tunnel mode gre ip' (point-to-point to hub) + 'ip nhrp nhs <hub-tunnel-ip>'",
        "NHRP = Next Hop Resolution Protocol — allows spoke-to-spoke direct tunnels",
      ],
      tags: ["virtualization", "dmvpn", "mgre", "nhrp", "hub-spoke", "tunnel"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-052",
      domain: "1.0",
      category: "Virtualization",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure a VXLAN flood-and-learn VTEP on a Nexus 9000 (NX-OS) switch. Create NVE " +
        "interface 1 sourced from Loopback0, and map VLAN 10 to VNI 10010 using multicast group " +
        "239.1.1.1 for BUM flooding.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "vlan 10",
            "vn-segment 10010",
            "interface nve1",
            "no shutdown",
            "source-interface Loopback0",
            "member vni 10010 mcast-group 239.1.1.1",
          ],
          explanation:
            "NX-OS VXLAN flood-and-learn binds the VLAN to a VNI with 'vn-segment' inside the " +
            "VLAN, then the NVE interface advertises the VTEP (using Loopback0 as the source IP) " +
            "and maps VNIs to multicast groups for BUM (Broadcast, Unknown-unicast, Multicast) " +
            "replication. Catalyst 9000 IOS-XE uses a different CLI (EVPN or LISP control plane).",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "vxlan vni",
          feedback:
            "IOS-XE VXLAN configuration uses 'member vni' under the NVE interface, " +
            "not a standalone 'vxlan vni' command.",
        },
        {
          pattern: "tunnel mode vxlan",
          feedback:
            "VXLAN does not use tunnel interfaces on IOS-XE. " +
            "It uses NVE (Network Virtualization Edge) interfaces: 'interface nve1'.",
        },
      ],
      hints: [
        "NVE interface: 'interface nve1'",
        "VNI mapping: 'member vni <id> mcast-group <ip>'",
        "VXLAN uses UDP port 4789 for encapsulation",
      ],
      tags: ["virtualization", "vxlan", "nve", "vtep", "vni", "overlay", "data-center"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    // ─────────────────────────────────────────────
    // Architecture  (4 expanded questions)
    // ─────────────────────────────────────────────

    {
      id: "cmd-exp-053",
      domain: "3.0",
      category: "QoS",
      difficulty: 3,
      mode: "command_builder",
      prompt:
        "Configure a QoS policy with DSCP-based classification. Create a class-map 'VOICE' " +
        "matching DSCP EF (46), a class-map 'VIDEO' matching DSCP AF41. " +
        "Create a policy-map 'QOS-OUT' prioritizing VOICE at 30% and guaranteeing VIDEO 20%. " +
        "Apply outbound to GigabitEthernet0/0.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "class-map match-all VOICE",
            "match dscp ef",
            "class-map match-all VIDEO",
            "match dscp af41",
            "policy-map QOS-OUT",
            "class VOICE",
            "priority percent 30",
            "class VIDEO",
            "bandwidth percent 20",
            "interface GigabitEthernet0/0",
            "service-policy output QOS-OUT",
          ],
          explanation:
            "QoS MQC (Modular QoS CLI): class-maps classify traffic, policy-maps define actions, " +
            "service-policy applies to interfaces. 'priority' provides LLQ (Low Latency Queuing) " +
            "for voice — strict priority with policing. 'bandwidth' provides minimum guaranteed " +
            "bandwidth without strict priority behavior.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "match dscp 46",
          feedback:
            "Use the DSCP keyword name: 'match dscp ef' (EF = Expedited Forwarding = DSCP 46). " +
            "Both numeric and name work, but names are cleaner and less error-prone.",
        },
        {
          pattern: "class VOICE.*bandwidth.*class VIDEO.*priority",
          feedback:
            "LLQ 'priority' should be applied to the voice class (lowest latency, strict). " +
            "'bandwidth' provides WFQ for other classes. Priority + bandwidth order matters.",
        },
      ],
      hints: [
        "'priority' = LLQ (strict priority, with implicit policing)",
        "'bandwidth' = WFQ minimum guarantee (no strict priority)",
        "DSCP keywords: ef (46), af41 (34), af31 (26), cs3 (24), default (0)",
      ],
      tags: ["architecture", "qos", "mqc", "dscp", "llq", "priority", "bandwidth", "class-map"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-054",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure AAA for device administration using TACACS+. Enable AAA, configure a " +
        "TACACS+ server named 'ISE' at 10.10.10.10 with key 'Admin@123'. " +
        "Set authentication for login to use TACACS+ with local fallback.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "aaa new-model",
            "tacacs server ISE",
            "address ipv4 10.10.10.10",
            "key Admin@123",
            "aaa authentication login default group tacacs+ local",
            "aaa authorization exec default group tacacs+ local",
          ],
          explanation:
            "AAA with TACACS+ provides centralized authentication and authorization. " +
            "'aaa new-model' enables AAA globally. Named TACACS+ server blocks (IOS-XE) allow " +
            "per-server configuration. 'local' fallback ensures admin access if TACACS+ is unreachable. " +
            "Authorization 'exec' controls whether a user gets a shell (exec) after authentication.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "tacacs-server host",
          feedback:
            "'tacacs-server host' is legacy global config syntax. Modern IOS-XE uses named TACACS+ " +
            "server blocks: 'tacacs server <name>' with sub-commands.",
        },
        {
          pattern: "aaa authentication login default group tacacs\\+$",
          feedback:
            "Without 'local' fallback, you could be locked out if the TACACS+ server is unreachable. " +
            "Always include 'local' as a fallback method for device administration.",
        },
      ],
      hints: [
        "Always include 'local' fallback to prevent lockout",
        "Named server block: 'tacacs server <name>' + 'address ipv4' + 'key'",
        "'aaa authorization exec' controls shell access — easy to forget",
      ],
      tags: ["architecture", "aaa", "tacacs+", "authentication", "authorization", "device-admin"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-055",
      domain: "1.0",
      category: "Architecture",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "In Cisco SD-WAN (Viptela), what is the name of the centralized controller component " +
        "responsible for the management plane — providing a GUI, REST API, and zero-touch provisioning?",
      context_mode: null,
      accepted_answers: [
        {
          commands: ["vManage"],
          explanation:
            "vManage is the SD-WAN management controller providing the single-pane-of-glass GUI, " +
            "REST API, device templates, policies, and zero-touch provisioning (ZTP). " +
            "Other SD-WAN components: vSmart (control plane/policy), vBond (orchestration/NAT traversal), " +
            "vEdge/cEdge (data plane WAN routers).",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "vSmart",
          feedback:
            "vSmart is the SD-WAN CONTROL plane controller that distributes routing and policy " +
            "via OMP (Overlay Management Protocol). vManage is the MANAGEMENT plane.",
        },
        {
          pattern: "vBond",
          feedback:
            "vBond is the ORCHESTRATION component handling initial device authentication and " +
            "NAT traversal. vManage is the management/GUI component.",
        },
      ],
      hints: [
        "SD-WAN controllers: vManage (mgmt), vSmart (control), vBond (orchestration)",
        "vManage = REST API + GUI + ZTP + templates",
      ],
      tags: ["architecture", "sd-wan", "vmanage", "sdwan", "controller", "management-plane"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-056",
      domain: "1.0",
      category: "Architecture",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "What Cisco DNA Center API endpoint base path is used for intent-based networking APIs " +
        "(the primary REST API for network operations such as retrieving device inventory)?",
      context_mode: null,
      accepted_answers: [
        {
          commands: ["/dna/intent/api/v1/"],
          explanation:
            "Cisco DNA Center Intent APIs use the base path '/dna/intent/api/v1/' for network " +
            "operations. Common endpoints include /network-device (device inventory), /site, " +
            "/topology, and /template-programmer. Authentication uses X-Auth-Token obtained " +
            "from POST to /dna/system/api/v1/auth/token.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "/api/v1/",
          feedback:
            "DNA Center Intent APIs use '/dna/intent/api/v1/', not a bare '/api/v1/' path. " +
            "The '/dna/intent/' prefix distinguishes intent APIs from other DNA Center API categories.",
        },
      ],
      hints: [
        "Base path: /dna/intent/api/v1/",
        "Auth token: POST to /dna/system/api/v1/auth/token",
        "Token passed in X-Auth-Token header for subsequent requests",
      ],
      tags: ["architecture", "dna-center", "rest-api", "intent-api", "automation", "programmability"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    // ─────────────────────────────────────────────
    // SYNTAX FIX  (6 expanded questions)
    // ─────────────────────────────────────────────

    {
      id: "cmd-exp-sf-001",
      domain: "3.0",
      category: "OSPF",
      difficulty: 2,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this OSPF virtual link configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "router ospf 1",
        "area 0 virtual-link 2.2.2.2",
        "area 1 range 10.1.0.0 0.0.255.255",
        "default-information originate always",
      ],
      error_lines: [
        {
          original: "area 0 virtual-link 2.2.2.2",
          corrected: "area 1 virtual-link 2.2.2.2",
          explanation:
            "The virtual link command specifies the TRANSIT area (the non-backbone area through " +
            "which the link passes), not the backbone area. 'area 0' is the backbone being extended; " +
            "the transit area is 1.",
        },
        {
          original: "area 1 range 10.1.0.0 0.0.255.255",
          corrected: "area 1 range 10.1.0.0 255.255.0.0",
          explanation:
            "'area range' uses a SUBNET MASK (255.255.0.0), not a wildcard mask (0.0.255.255). " +
            "Wildcard masks are only used in OSPF 'network' statements.",
        },
      ],
      accepted_answers: [
        {
          commands: [
            "area 1 virtual-link 2.2.2.2",
            "area 1 range 10.1.0.0 255.255.0.0",
          ],
          explanation:
            "Two errors: (1) virtual-link must reference the transit area (area 1), not area 0. " +
            "(2) area range uses a subnet mask, not a wildcard mask.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "Virtual link area = TRANSIT area, not area 0",
        "'area range' uses subnet mask; 'network' statement uses wildcard",
      ],
      tags: ["ospf", "virtual-link", "area-range", "subnet-mask", "syntax-fix"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-sf-002",
      domain: "3.0",
      category: "EIGRP",
      difficulty: 2,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this EIGRP named-mode configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "router eigrp 100",
        "address-family ipv4 unicast autonomous-system 100",
        "eigrp router-id 1.1.1.1",
        "network 10.0.0.0 0.0.0.255",
        "eigrp stub receive-only",
      ],
      error_lines: [
        {
          original: "router eigrp 100",
          corrected: "router eigrp MYPROCESS",
          explanation:
            "EIGRP named mode uses a NAME (string) after 'router eigrp', not a number. " +
            "The AS number is specified inside the address-family with 'autonomous-system 100'. " +
            "'router eigrp 100' is classic EIGRP mode syntax.",
        },
      ],
      accepted_answers: [
        {
          commands: ["router eigrp MYPROCESS"],
          explanation:
            "EIGRP named mode begins with 'router eigrp <name>' where the name is a string. " +
            "Replace 'router eigrp 100' with 'router eigrp <any-name>' to use named mode.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "router eigrp [0-9]+",
          feedback:
            "In named mode the argument to 'router eigrp' is a name string, not a number. " +
            "The AS number goes inside: 'address-family ipv4 unicast autonomous-system <AS>'.",
        },
      ],
      hints: [
        "Named mode: 'router eigrp <NAME>' (string)",
        "Classic mode: 'router eigrp <AS>' (number)",
        "In named mode the AS is inside the address-family sub-mode",
      ],
      tags: ["eigrp", "named-mode", "address-family", "autonomous-system", "syntax-fix"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-sf-003",
      domain: "3.0",
      category: "BGP",
      difficulty: 3,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this BGP community configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "route-map SET-COMM permit 10",
        "match ip address prefix-list PL-EXPORT",
        "set community 65001 100",
        "router bgp 65001",
        "neighbor 203.0.113.1 remote-as 65002",
        "neighbor 203.0.113.1 route-map SET-COMM out",
        "no neighbor 203.0.113.1 send-community",
      ],
      error_lines: [
        {
          original: "set community 65001 100",
          corrected: "set community 65001:100",
          explanation:
            "'set community' requires the community value in AA:NN format using a colon separator. " +
            "'set community 65001 100' sets TWO separate community values (65001 and 100), " +
            "not the intended single community 65001:100.",
        },
        {
          original: "no neighbor 203.0.113.1 send-community",
          corrected: "neighbor 203.0.113.1 send-community",
          explanation:
            "BGP does not send community attributes to neighbors by default, and the 'no' form " +
            "here explicitly disables it. 'neighbor 203.0.113.1 send-community' (without 'no') " +
            "must be configured for communities to be included in UPDATE messages.",
        },
      ],
      accepted_answers: [
        {
          commands: [
            "set community 65001:100",
            "neighbor 203.0.113.1 send-community",
          ],
          explanation:
            "Two fixes: (1) Use colon format '65001:100' for community values. " +
            "(2) Enable 'neighbor ... send-community' so the community attribute is actually transmitted.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "Community format: AA:NN (colon-separated ASN:value)",
        "Communities are NEVER sent to neighbors without 'send-community'",
      ],
      tags: ["bgp", "community", "send-community", "route-map", "syntax-fix"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-sf-004",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this DHCP snooping configuration:",
      context_mode: "Switch(config)#",
      config_block: [
        "no ip dhcp snooping",
        "ip dhcp snooping vlan 10,20",
        "interface GigabitEthernet1/0/24",
        "ip dhcp snooping trust",
        "interface GigabitEthernet1/0/1",
        "ip dhcp snooping limit rate 10",
      ],
      error_lines: [
        {
          original: "no ip dhcp snooping",
          corrected: "ip dhcp snooping",
          explanation:
            "The global 'ip dhcp snooping' command must be enabled for the feature to work — " +
            "the 'no' form disables it globally, so the per-VLAN, trust, and rate-limit config " +
            "have no effect. Remove the 'no' to enable DHCP snooping globally.",
        },
      ],
      accepted_answers: [
        {
          commands: ["ip dhcp snooping"],
          explanation:
            "The global 'ip dhcp snooping' command must precede any per-VLAN configuration. " +
            "Add it as the first DHCP snooping command.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "DHCP snooping requires global enable first, then per-VLAN",
        "Missing global 'ip dhcp snooping' is the most common configuration error",
      ],
      tags: ["security", "dhcp-snooping", "global-enable", "vlan", "syntax-fix"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-sf-005",
      domain: "6.0",
      category: "Network Assurance",
      difficulty: 2,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this Flexible NetFlow configuration:",
      context_mode: "Router(config)#",
      config_block: [
        "flow record MY-RECORD",
        "match ipv4 source address",
        "match ipv4 destination address",
        "collect counter bytes",
        "flow exporter MY-EXPORTER",
        "destination 10.0.0.100",
        "transport udp 9996",
        "flow monitor MY-MONITOR",
        "record MY-RECORD",
        "exporter WRONG-EXPORTER",
        "interface GigabitEthernet0/0",
        "ip flow-export MY-MONITOR input",
      ],
      error_lines: [
        {
          original: "ip flow-export MY-MONITOR input",
          corrected: "ip flow monitor MY-MONITOR input",
          explanation:
            "'ip flow-export' is legacy NetFlow syntax for specifying the export destination. " +
            "Flexible NetFlow uses 'ip flow monitor <monitor-name> input|output' to apply a " +
            "flow monitor to an interface.",
        },
        {
          original: "exporter WRONG-EXPORTER",
          corrected: "exporter MY-EXPORTER",
          explanation:
            "The flow monitor references a non-existent exporter name, so captured flow data " +
            "is never exported. The configured exporter is 'MY-EXPORTER'.",
        },
      ],
      accepted_answers: [
        {
          commands: [
            "ip flow monitor MY-MONITOR input",
            "exporter MY-EXPORTER",
          ],
          explanation:
            "Two fixes: (1) interface command must be 'ip flow monitor', not 'ip flow-export'. " +
            "(2) The flow monitor's 'exporter' line must reference a configured exporter name.",
          is_primary: true,
        },
      ],
      common_mistakes: [],
      hints: [
        "'ip flow-export' is legacy; Flexible NetFlow uses 'ip flow monitor <name> input'",
        "Flow monitor must explicitly reference an exporter with 'exporter <name>'",
      ],
      tags: ["network-assurance", "netflow", "flexible-netflow", "flow-monitor", "syntax-fix"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    {
      id: "cmd-exp-sf-006",
      domain: "2.0",
      category: "STP",
      difficulty: 2,
      mode: "syntax_fix",
      prompt: "Find and fix the errors in this MST configuration:",
      context_mode: "Switch(config)#",
      config_block: [
        "spanning-tree mode mstp",
        "spanning-tree mst configuration",
        "name CAMPUS",
        "revision 1",
        "instance 1 vlan 1-100",
        "instance 2 vlan 101-200",
      ],
      error_lines: [
        {
          original: "spanning-tree mode mstp",
          corrected: "spanning-tree mode mst",
          explanation:
            "The IOS-XE keyword for Multiple Spanning Tree is 'mst', not 'mstp'. " +
            "'spanning-tree mode mstp' is invalid and will be rejected. " +
            "Valid modes: pvst, rapid-pvst, mst.",
        },
      ],
      accepted_answers: [
        {
          commands: ["spanning-tree mode mst"],
          explanation:
            "Replace 'spanning-tree mode mstp' with 'spanning-tree mode mst'. " +
            "The 'p' is not part of the IOS-XE keyword even though the standard is called 802.1s MSTP.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "spanning-tree mode mstp",
          feedback:
            "IOS-XE uses 'mst' not 'mstp' as the mode keyword. " +
            "Valid options: spanning-tree mode pvst | rapid-pvst | mst",
        },
      ],
      hints: [
        "IOS-XE STP modes: pvst, rapid-pvst, mst (not mstp)",
        "MST config sub-mode commands: name, revision, instance",
      ],
      tags: ["stp", "mst", "mstp", "802.1s", "spanning-tree-mode", "syntax-fix"],
      is_starred: false,
      stats: { times_attempted: 0, times_correct: 0, avg_time_seconds: 0, best_time_seconds: null },
    },

    // ─────────────────────────────────────────────
    // VLAN / Trunking  (5 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-127",
      domain: "3.0",
      category: "Switching",
      difficulty: 1,
      mode: "command_builder",
      prompt: "Create VLAN 100 named \"ENGINEERING\" on a switch.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: ["vlan 100", "name ENGINEERING"],
          explanation:
            "The 'vlan 100' command enters VLAN configuration mode and creates the VLAN. " +
            "The 'name' command assigns a descriptive name to the VLAN from VLAN config mode.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "vlan name",
          feedback:
            "The 'name' command must be issued from VLAN configuration mode (after entering 'vlan 100'), " +
            "not from global config. Enter the VLAN first, then assign the name.",
        },
      ],
      hints: [
        "You need to enter VLAN configuration mode first",
        "The name command is a sub-command under the VLAN",
      ],
      tags: ["vlan", "switching", "vlan-name"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-128",
      domain: "3.0",
      category: "Switching",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Configure interface Gi0/1 as an 802.1Q trunk with native VLAN 99.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "interface GigabitEthernet0/1",
            "switchport mode trunk",
            "switchport trunk native vlan 99",
          ],
          explanation:
            "Enter interface config, set the port to trunk mode, then assign the native VLAN. " +
            "On modern IOS-XE switches that only support 802.1Q, the encapsulation command is not needed.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "encapsulation dot1q",
          feedback:
            "On older platforms you need 'switchport trunk encapsulation dot1q', but on newer IOS-XE " +
            "switches that only support 802.1Q this command is not available or required.",
        },
      ],
      hints: [
        "On modern IOS-XE, dot1q is the only option so encapsulation command may not be needed",
        "Native VLAN is the VLAN that carries untagged traffic on a trunk",
      ],
      tags: ["trunk", "802.1q", "native-vlan", "switching"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-129",
      domain: "3.0",
      category: "Switching",
      difficulty: 1,
      mode: "command_builder",
      prompt: "Configure an access port on Gi0/2 for VLAN 100.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "interface GigabitEthernet0/2",
            "switchport mode access",
            "switchport access vlan 100",
          ],
          explanation:
            "Enter interface config, set the port to access mode, and assign it to VLAN 100. " +
            "Access ports carry traffic for a single VLAN only.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "mode trunk",
          feedback:
            "An access port should use 'switchport mode access', not 'switchport mode trunk'. " +
            "Trunk ports carry multiple VLANs; access ports carry only one.",
        },
      ],
      hints: [
        "Access ports belong to exactly one VLAN",
        "Set the mode before assigning the VLAN",
      ],
      tags: ["access-port", "vlan", "switching"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-130",
      domain: "3.0",
      category: "Switching",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Disable DTP on a trunk port (Gi0/1) so it does not send DTP frames.",
      context_mode: "Switch(config-if)#",
      accepted_answers: [
        {
          commands: ["switchport nonegotiate"],
          explanation:
            "The 'switchport nonegotiate' command prevents the port from sending DTP frames " +
            "while keeping the port in its current trunk or access mode.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "no switchport",
          feedback:
            "'no switchport' converts the port to a routed (Layer 3) port, removing all Layer 2 " +
            "switching capabilities. Use 'switchport nonegotiate' to disable DTP only.",
        },
      ],
      hints: [
        "This prevents the port from sending DTP frames while remaining a trunk",
        "The command does not change the port mode — it only stops negotiation",
      ],
      tags: ["dtp", "trunk", "nonegotiate", "switching", "security"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-131",
      domain: "3.0",
      category: "Switching",
      difficulty: 1,
      mode: "speed_round",
      prompt: "Verify which VLANs are allowed on a trunk interface.",
      context_mode: "Switch#",
      accepted_answers: [
        {
          commands: ["show interfaces trunk"],
          explanation:
            "This command displays all trunk ports, their native VLAN, and which VLANs are " +
            "allowed, active, and forwarding on each trunk.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show vlan",
          feedback:
            "'show vlan' shows VLAN database info and access port assignments. " +
            "Use 'show interfaces trunk' to see trunk-specific VLAN information.",
        },
      ],
      hints: [
        "This shows trunk ports, native VLAN, and allowed/active VLANs",
      ],
      tags: ["trunk", "vlan", "verification", "show-command"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // NAT  (4 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-132",
      domain: "3.0",
      category: "IP Services",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure PAT (NAT overload) using an ACL. Inside interface is Gi0/0, outside is Gi0/1. " +
        "Translate 10.0.0.0/24 to the outside interface IP.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "access-list 1 permit 10.0.0.0 0.0.0.255",
            "ip nat inside source list 1 interface GigabitEthernet0/1 overload",
            "interface GigabitEthernet0/0",
            "ip nat inside",
            "interface GigabitEthernet0/1",
            "ip nat outside",
          ],
          explanation:
            "Define an ACL matching the inside network, create the NAT overload rule referencing " +
            "the outside interface, then mark interfaces as inside/outside. The 'overload' keyword " +
            "enables PAT so multiple hosts share one public IP via port translation.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "source list 1 interface.*(?!overload)",
          feedback:
            "Without the 'overload' keyword you get 1:1 dynamic NAT, not PAT. " +
            "Add 'overload' to enable Port Address Translation.",
        },
      ],
      hints: [
        "'overload' enables PAT — multiple inside hosts share one IP via port translation",
        "Both inside and outside interfaces must be marked with 'ip nat inside/outside'",
      ],
      tags: ["nat", "pat", "overload", "acl", "ip-services"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-133",
      domain: "3.0",
      category: "IP Services",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Configure a static NAT entry mapping inside host 192.168.1.10 to public IP 203.0.113.10.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "ip nat inside source static 192.168.1.10 203.0.113.10",
          ],
          explanation:
            "Static NAT creates a permanent one-to-one mapping. The syntax is " +
            "'ip nat inside source static <inside-local> <inside-global>'.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "static 203\\.0\\.113\\.10 192\\.168\\.1\\.10",
          feedback:
            "The order is inside-local first, then inside-global. " +
            "You have the addresses reversed — the private IP comes before the public IP.",
        },
      ],
      hints: [
        "Syntax: ip nat inside source static <local-IP> <global-IP>",
        "The inside local address (private) comes first",
      ],
      tags: ["nat", "static-nat", "ip-services"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-134",
      domain: "3.0",
      category: "IP Services",
      difficulty: 1,
      mode: "speed_round",
      prompt: "Verify current NAT translations on a router.",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: ["show ip nat translations"],
          explanation:
            "Displays the NAT translation table showing inside local/global and " +
            "outside local/global address mappings.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show nat",
          feedback:
            "The full command is 'show ip nat translations'. " +
            "'show nat' alone is not valid IOS-XE syntax.",
        },
      ],
      hints: ["This shows the active NAT translation table entries"],
      tags: ["nat", "verification", "show-command", "ip-services"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-135",
      domain: "3.0",
      category: "IP Services",
      difficulty: 1,
      mode: "speed_round",
      prompt: "Clear all dynamic NAT translations.",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: ["clear ip nat translation *"],
          explanation:
            "The '*' wildcard clears all dynamic NAT translation entries. " +
            "Static NAT entries are not affected by this command.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "clear ip nat",
          feedback:
            "You need to specify what to clear. Use 'clear ip nat translation *' " +
            "to clear all dynamic entries.",
        },
      ],
      hints: [
        "Use * to clear all dynamic entries. Static entries are not cleared.",
      ],
      tags: ["nat", "clear-command", "ip-services"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // NTP  (3 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-136",
      domain: "4.0",
      category: "Network Assurance",
      difficulty: 1,
      mode: "command_builder",
      prompt: "Configure the router to synchronize with NTP server 10.1.1.1.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: ["ntp server 10.1.1.1"],
          explanation:
            "The 'ntp server' command configures the router as an NTP client that synchronizes " +
            "its clock to the specified NTP server.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ntp peer",
          feedback:
            "'ntp peer' creates a mutual (symmetric) synchronization relationship. " +
            "Use 'ntp server' for a client-server relationship where this router is the client.",
        },
      ],
      hints: [
        "'ntp server' makes this router a client; 'ntp peer' is for mutual sync",
        "The server address follows the 'ntp server' keyword directly",
      ],
      tags: ["ntp", "time", "network-assurance"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-137",
      domain: "4.0",
      category: "Network Assurance",
      difficulty: 1,
      mode: "speed_round",
      prompt:
        "Verify NTP synchronization status showing stratum, reference source, and offset.",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: ["show ntp status"],
          explanation:
            "Displays NTP synchronization state, stratum level, reference clock source, " +
            "and clock offset in milliseconds.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show ntp associations",
          feedback:
            "'show ntp associations' shows configured NTP peers/servers but not the full " +
            "synchronization status. Use 'show ntp status' for stratum, offset, and sync state.",
        },
      ],
      hints: ["This command gives a summary of the NTP sync state"],
      tags: ["ntp", "verification", "show-command", "network-assurance"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-138",
      domain: "4.0",
      category: "Network Assurance",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure NTP authentication: define key 1 with password \"Cisco123\", mark it trusted, " +
        "and apply it to the NTP server 10.1.1.1.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "ntp authenticate",
            "ntp authentication-key 1 md5 Cisco123",
            "ntp trusted-key 1",
            "ntp server 10.1.1.1 key 1",
          ],
          explanation:
            "Enable NTP authentication globally, define an MD5 key, mark it as trusted, " +
            "and associate it with the NTP server. All four steps are required for authenticated NTP.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "ntp server.*(?!key)",
          feedback:
            "You must associate the authentication key with the server using the 'key' keyword. " +
            "Without 'key 1' on the ntp server command, authentication is not applied to that server.",
        },
      ],
      hints: [
        "Four commands are needed: enable auth, define key, trust key, apply to server",
        "The 'ntp authenticate' command enables NTP authentication globally",
      ],
      tags: ["ntp", "authentication", "md5", "security", "network-assurance"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // EtherChannel  (4 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-139",
      domain: "3.0",
      category: "Switching",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Create a Layer 2 EtherChannel using LACP on interfaces Gi0/1-2 in active mode.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "interface range GigabitEthernet0/1-2",
            "channel-group 1 mode active",
          ],
          explanation:
            "Select the interfaces with 'interface range', then assign them to a channel-group " +
            "with LACP active mode. Active mode initiates LACP negotiation.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "mode on",
          feedback:
            "'mode on' forces the EtherChannel without any negotiation protocol (no LACP or PAgP). " +
            "Use 'mode active' for LACP negotiation.",
        },
      ],
      hints: [
        "LACP modes: active (initiate) or passive (wait). At least one side must be active.",
        "Use 'interface range' to configure multiple ports at once",
      ],
      tags: ["etherchannel", "lacp", "port-channel", "switching"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-140",
      domain: "3.0",
      category: "Switching",
      difficulty: 1,
      mode: "speed_round",
      prompt: "Verify EtherChannel status showing which ports are bundled.",
      context_mode: "Switch#",
      accepted_answers: [
        {
          commands: ["show etherchannel summary"],
          explanation:
            "Displays a one-line summary per port-channel showing member ports, " +
            "their status flags (P = bundled, I = stand-alone), and the protocol in use.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show port-channel",
          feedback:
            "Use 'show etherchannel summary' for a quick overview of all port-channels " +
            "and their member port status.",
        },
      ],
      hints: ["Look for the 'P' flag — it means the port is bundled in the channel"],
      tags: ["etherchannel", "verification", "show-command", "switching"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-141",
      domain: "3.0",
      category: "Switching",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Configure EtherChannel load balancing to use source-destination IP.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: ["port-channel load-balance src-dst-ip"],
          explanation:
            "This global command sets the hashing algorithm for EtherChannel load distribution. " +
            "Source-destination IP provides good distribution for most traffic patterns.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "interface",
          feedback:
            "EtherChannel load balancing is configured globally, not per-interface. " +
            "Use 'port-channel load-balance' from global config mode.",
        },
      ],
      hints: [
        "This is a global command, not per-interface.",
        "Common options: src-mac, dst-mac, src-dst-mac, src-ip, dst-ip, src-dst-ip",
      ],
      tags: ["etherchannel", "load-balance", "switching"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-142",
      domain: "3.0",
      category: "Switching",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Configure a Layer 3 EtherChannel using LACP on Gi0/1-2, assign IP 10.0.0.1/30.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "interface range GigabitEthernet0/1-2",
            "no switchport",
            "channel-group 1 mode active",
            "interface Port-channel1",
            "no switchport",
            "ip address 10.0.0.1 255.255.255.252",
          ],
          explanation:
            "Convert the physical interfaces to routed ports with 'no switchport', assign them to " +
            "a channel-group with LACP active, then configure the Port-channel interface as a " +
            "routed port with an IP address.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "switchport",
          feedback:
            "For a Layer 3 EtherChannel, member ports and the Port-channel interface must be " +
            "routed ports. Use 'no switchport' on both the physical interfaces and the Port-channel.",
        },
      ],
      hints: [
        "Layer 3 EtherChannels require 'no switchport' on member ports",
        "The IP address goes on the Port-channel interface, not the physical ports",
      ],
      tags: ["etherchannel", "lacp", "layer3", "port-channel", "switching"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // Port Security  (3 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-143",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Enable port security on Gi0/1, limit to 2 MAC addresses, set violation mode to restrict.",
      context_mode: "Switch(config)#",
      accepted_answers: [
        {
          commands: [
            "interface GigabitEthernet0/1",
            "switchport mode access",
            "switchport port-security",
            "switchport port-security maximum 2",
            "switchport port-security violation restrict",
          ],
          explanation:
            "Port security requires the port to be in access mode. Enable it with " +
            "'switchport port-security', set the maximum MAC addresses, and configure " +
            "the violation action. Restrict mode drops violating traffic and logs it.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "mode trunk",
          feedback:
            "Port security can only be enabled on access ports or trunk ports with extra config. " +
            "The standard approach is to set the port to access mode first.",
        },
      ],
      hints: [
        "The port must be in access mode before enabling port security",
        "Violation modes: protect (drop silently), restrict (drop + log), shutdown (err-disable)",
      ],
      tags: ["port-security", "mac-address", "security", "switching"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-144",
      domain: "5.0",
      category: "Security",
      difficulty: 1,
      mode: "command_builder",
      prompt: "Enable sticky MAC learning on interface Gi0/1.",
      context_mode: "Switch(config-if)#",
      accepted_answers: [
        {
          commands: ["switchport port-security mac-address sticky"],
          explanation:
            "Sticky MAC learning dynamically learns MAC addresses and adds them to the " +
            "running configuration. This persists across link flaps but requires " +
            "'write memory' to survive a reboot.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "mac-address \\w{4}",
          feedback:
            "Use the 'sticky' keyword to enable dynamic learning. " +
            "You don't need to manually specify a MAC address.",
        },
      ],
      hints: [
        "Sticky MACs are dynamically learned and added to running-config — save with 'write memory'.",
        "This is a sub-command of port-security",
      ],
      tags: ["port-security", "sticky-mac", "security", "switching"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-145",
      domain: "5.0",
      category: "Security",
      difficulty: 1,
      mode: "speed_round",
      prompt: "Verify port security status on interface Gi0/1.",
      context_mode: "Switch#",
      accepted_answers: [
        {
          commands: ["show port-security interface GigabitEthernet0/1"],
          explanation:
            "Shows port security status for the specified interface including " +
            "maximum MACs, current count, violation count, and violation action.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show port-security$",
          feedback:
            "Without specifying an interface, 'show port-security' gives a summary table. " +
            "Add 'interface GigabitEthernet0/1' for detailed per-port information.",
        },
      ],
      hints: ["Specify the interface for detailed port security information"],
      tags: ["port-security", "verification", "show-command", "security"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // CoPP  (2 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-146",
      domain: "5.0",
      category: "Security",
      difficulty: 1,
      mode: "speed_round",
      prompt: "Show the current CoPP policy applied to the control plane.",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: ["show policy-map control-plane"],
          explanation:
            "Displays the service-policy applied to the control plane, including " +
            "class-maps, match criteria, police rates, and packet statistics.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show policy-map interface",
          feedback:
            "CoPP policies are applied to 'control-plane', not to interfaces. " +
            "Use 'show policy-map control-plane' to view CoPP stats.",
        },
      ],
      hints: [
        "CoPP uses service-policy applied to 'control-plane', not interfaces.",
      ],
      tags: ["copp", "control-plane", "qos", "security", "show-command"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-147",
      domain: "5.0",
      category: "Security",
      difficulty: 2,
      mode: "command_builder",
      prompt:
        "Apply a CoPP policy-map named \"COPP-POLICY\" to the control plane.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "control-plane",
            "service-policy input COPP-POLICY",
          ],
          explanation:
            "Enter the control-plane configuration mode, then apply the policy-map as an " +
            "input service-policy. CoPP policies are always applied in the input direction " +
            "because they filter traffic destined to the route processor.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "interface",
          feedback:
            "CoPP is applied to the control plane, not to a physical interface. " +
            "Use the 'control-plane' command to enter control-plane config mode.",
        },
      ],
      hints: [
        "CoPP policies are applied under 'control-plane' config mode",
        "The direction is always 'input' — traffic going TO the control plane",
      ],
      tags: ["copp", "control-plane", "policy-map", "security"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    // ─────────────────────────────────────────────
    // QoS  (3 questions)
    // ─────────────────────────────────────────────
    {
      id: "cmd-148",
      domain: "3.0",
      category: "QoS",
      difficulty: 1,
      mode: "command_builder",
      prompt: "Create a class-map named 'VOICE' (match-all) that matches DSCP EF traffic.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "class-map match-all VOICE",
            "match dscp ef",
          ],
          explanation:
            "The 'class-map match-all' creates a class that requires all match conditions to be true. " +
            "DSCP EF (Expedited Forwarding, decimal 46) is used for voice traffic requiring " +
            "low latency, low jitter, and low loss.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "match dscp 46",
          feedback:
            "While DSCP 46 is numerically correct (it equals EF), using the keyword 'ef' " +
            "is preferred for readability. Both are accepted by IOS-XE.",
        },
      ],
      hints: [
        "DSCP EF = 46 decimal, used for voice/real-time traffic",
        "'match-all' means all conditions must match; 'match-any' means at least one",
      ],
      tags: ["qos", "class-map", "dscp", "voice"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-149",
      domain: "3.0",
      category: "QoS",
      difficulty: 1,
      mode: "command_builder",
      prompt:
        "Apply a QoS service-policy named \"QOS-OUT\" as output on interface Gi0/0.",
      context_mode: "Router(config)#",
      accepted_answers: [
        {
          commands: [
            "interface GigabitEthernet0/0",
            "service-policy output QOS-OUT",
          ],
          explanation:
            "Enter interface config and apply the policy-map in the output direction. " +
            "Egress QoS policies handle queuing, shaping, and scheduling of outbound traffic.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "service-policy input",
          feedback:
            "The question asks for an output policy. Input policies are typically used for " +
            "classification and marking; output policies handle queuing and shaping.",
        },
      ],
      hints: [
        "QoS policies are applied per-interface with a direction (input or output)",
        "Shaping and queuing are typically applied in the output direction",
      ],
      tags: ["qos", "service-policy", "policy-map", "interface"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },

    {
      id: "cmd-150",
      domain: "3.0",
      category: "QoS",
      difficulty: 1,
      mode: "speed_round",
      prompt: "Verify QoS policy-map statistics on interface Gi0/0.",
      context_mode: "Router#",
      accepted_answers: [
        {
          commands: ["show policy-map interface GigabitEthernet0/0"],
          explanation:
            "Displays applied policy-maps on the interface with per-class packet/byte counters, " +
            "queue depths, drop counts, and police/shape statistics.",
          is_primary: true,
        },
      ],
      common_mistakes: [
        {
          pattern: "show policy-map$",
          feedback:
            "'show policy-map' without an interface shows the policy-map configuration but not " +
            "operational statistics. Add 'interface' for live counters.",
        },
      ],
      hints: ["Add the interface name to see live packet/byte statistics"],
      tags: ["qos", "policy-map", "verification", "show-command"],
      is_starred: false,
      stats: {
        times_attempted: 0,
        times_correct: 0,
        avg_time_seconds: 0,
        best_time_seconds: null,
      },
    },
  ];
}
